// src/diagrams/contactFlowLucidParser.js - Convert Amazon Connect flows to Lucid Charts format

import dagre from 'dagre';
import { EdgeRoutingSystem } from './routing/EdgeRoutingSystem';
import { ContactFlowParsingUtils } from './contactFlowParsingUtils';
import { measureLucidHtml } from './utils/lucidTextMeasurement';

/**
 * Parser for converting Amazon Connect Contact Flows directly to Lucid Charts format
 * This bypasses React Flow and creates professional Lucid diagrams with rich content
 */
export class ContactFlowLucidParser {
  constructor(flowContent, exportedData = null, instanceId = null, layoutDirection = 'TB', routingConfig = null, verboseLogging = false, flowMetadata = null) {
    this.flow = typeof flowContent === 'string' ? JSON.parse(flowContent) : flowContent;
    this.actions = new Map();
    this.metadata = this.flow.Metadata?.ActionMetadata || {};
    this.startAction = this.flow.StartAction;
    this.flowData = flowMetadata; // Store outer flow metadata (Name, Type, Description)
    
    this.exportedData = exportedData;
    this.instanceId = instanceId;
    this.layoutDirection = layoutDirection;
    this.verboseLogging = verboseLogging; // Add verbose logging control
    
    // Build actions map for easier lookup
    if (this.flow.Actions) {
      this.flow.Actions.forEach(action => {
        if (action && action.Identifier) {
          this.actions.set(action.Identifier, action);
        } else {
          this.log('warn', 'Invalid action found:', action);
        }
      });
    }
    
    // Independent routing system instance for Lucid coordinate calculations
    // Use provided routing config or optimized defaults for obstacle avoidance
    const defaultRoutingConfig = {
      gridSize: 18,              // Coarser grid keeps A* fast/convergent over the Lucid export's
                                 // larger (text-fit-inflated) node coordinates; 18px is still
                                 // finer than the routing margin so paths stay accurate
      nodePadding: 40,           // Routing margin around shapes; tight enough to find gaps,
                                 // wide enough that A* detours clearly clear the shapes
      enableBundling: false,     // Disable bundling for Lucid (React Flow specific feature)
      bundleThreshold: 0.6,
      maxBundleSize: 8,
      crossingPenalty: 30,       // Strongly discourage line crossings (was 20)
      turnPenalty: 3,            // Slightly lower to allow necessary turns (was 4)
      proximityPenalty: 15,      // Higher penalty to stay away from shapes (was 12)
      routingPolicy: 'SMART',
      diagonalMovement: false,    // Orthogonal movement for cleaner elbow lines
      preserveOrthogonal: true    // Keep A*'s real 90° corners (don't collapse to a diagonal),
                                  // so elbow control points route around shapes as intended
    };
    
    const finalRoutingConfig = routingConfig ? { ...defaultRoutingConfig, ...routingConfig } : defaultRoutingConfig;
    this.routingSystem = new EdgeRoutingSystem(finalRoutingConfig);
    
    // Lucid-specific constants - updated for better text spacing and overflow prevention
    this.LUCID_GRID_SIZE = 20; // Lucid coordinate grid size
    this.NODE_MIN_WIDTH = 140; // Increased from 120 for better text spacing
    this.NODE_MIN_HEIGHT = 80; // Increased from 60 for better text spacing
    this.NODE_PADDING = 25;    // Increased from 20 for better margins
    this.LINE_SPACING = 30;
    this.LINE_STUB = 56;       // straight distance a line travels out of a node before it may
                               // turn — keeps exit-point labels on the line and exits perpendicular

    // Info panels (Summary / AI Summary / Legend) sit to the LEFT of the diagram.
    // Uniform width + a real gap, anchored by their RIGHT edge, so they never overlap
    // the flow (the AI Summary used to be 700px wide and ran into the diagram).
    this.PANEL_WIDTH = 360;
    this.PANEL_GAP = 80; // gap between panels' right edge and the diagram's left edge
  }

  /**
   * Left X for an info panel so its right edge clears the diagram by PANEL_GAP.
   */
  getPanelX(bounds) {
    return bounds.minX - this.PANEL_GAP - this.PANEL_WIDTH;
  }

  /**
   * Centralized logging method with verbose control
   */
  log(level, message, ...args) {
    if (level === 'error') {
      console.error(message, ...args);
    } else if (level === 'warn') {
      if (this.verboseLogging) {
        console.warn(message, ...args);
      } else {
        console.debug(message, ...args);
      }
    } else if (level === 'info') {
      if (this.verboseLogging) {
        console.info(message, ...args);
      }
    } else if (level === 'debug') {
      if (this.verboseLogging) {
        console.debug(message, ...args);
      }
    }
  }

  /**
   * Convert Contact Flow to Lucid Charts format with intelligent layout
   */
  convertToLucid() {
    try {
      this.log('info', '=== Starting Lucid document conversion ===');
      this.log('info', `Flow: ${this.flowData?.Name || 'Unknown'} (${this.flowData?.Type || 'Unknown'})`);
      
      // Initialize tracking variables
      this.createdNodeIds = new Set();
      
      // Create the base document
      const document = this.createLucidDocument();
      
      // Validate and sanitize the document
      const validatedDocument = this.validateAndSanitizeLucidDocument(document);
      
      // Generate final conversion report
      this.generateConversionReport();
      
      this.log('info', '=== Lucid document conversion completed successfully ===');
      
      return validatedDocument;
    } catch (error) {
      this.log('error', '=== Lucid document conversion failed ===');
      this.log('error', `Error: ${error.message}`);
      
      // Generate diagnostic report even on failure
      this.generateConversionReport(error);
      
      throw error;
    }
  }

  generateConversionReport(error = null) {
    this.log('info', '=== LUCID CONVERSION DIAGNOSTIC REPORT ===');
    
    // Basic flow information
    this.log('info', `Flow Name: ${this.flowData?.Name || 'Unknown'}`);
    this.log('info', `Flow Type: ${this.flowData?.Type || 'Unknown'}`);
    this.log('info', `Flow ID: ${this.flowData?.Id || 'Unknown'}`);
    
    // Node creation statistics
    const totalActions = this.getActions().length;
    const createdNodes = this.createdNodeIds ? this.createdNodeIds.size : 0;
    
    this.log('info', `--- Node Creation Statistics ---`);
    this.log('info', `Total Actions in Flow: ${totalActions}`);
    this.log('info', `Successfully Created Nodes: ${createdNodes}`);
    
    // Transition statistics
    const allTransitions = this.getAllTransitions();
    this.log('info', `--- Transition Statistics ---`);
    this.log('info', `Total Valid Transitions: ${allTransitions.length}`);
    
    // Transition type breakdown
    const transitionTypes = {};
    allTransitions.forEach(t => {
      transitionTypes[t.type] = (transitionTypes[t.type] || 0) + 1;
    });
    Object.entries(transitionTypes).forEach(([type, count]) => {
      this.log('info', `  ${type}: ${count}`);
    });
    
    // Error information if conversion failed
    if (error) {
      this.log('error', `--- Conversion Error ---`);
      this.log('error', `Error Type: ${error.constructor.name}`);
      this.log('error', `Error Message: ${error.message}`);
    }
    
    // Status summary
    const failedNodes = totalActions - (createdNodes - 1); // Subtract 1 for entry node
    this.log('info', `--- Conversion Status ---`);
    if (failedNodes > 0) {
      this.log('warn', `• ${failedNodes} nodes failed validation - check source data quality`);
    } else {
      this.log('info', `• All nodes processed successfully - conversion is clean`);
    }
    
    this.log('info', '=== END DIAGNOSTIC REPORT ===');
  }

  /**
   * Create Lucid document structure
   */
  createLucidDocument() {
    const actions = this.getActions();
    const transitions = this.getAllTransitions();

    this.log('info', `Creating Lucid document with ${actions.length} actions and ${transitions.length} transitions`);

    // Create nodes (this populates createdNodeIds tracking)
    const shapes = this.createLucidNodes(actions);

    // Apply dagre layout for positioning and spacing. Runs on the measured node
    // dimensions (set in createLucidNode) using the same adaptive spacing as the viewer.
    this.optimizeLayoutPositions(shapes, transitions);

    // Analyze edge connections for label shape positioning
    const connectionAnalysis = this.analyzeEdgeConnections(transitions);

    // Create lines (transitions are now pre-validated, so this should not create null lines)
    const lines = this.createLucidLines(transitions, shapes);

    // Filter out any null lines just to be safe
    const validLines = lines.filter(line => line !== null);

    // Create rotated label shapes positioned at node exit points
    // These replace inline line labels for better readability with rotation
    const labelShapes = this.createLabelShapes(transitions, shapes, connectionAnalysis);

    // Calculate diagram bounds for positioning Summary and Legend panels
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    
    shapes.forEach(shape => {
      // Use boundingBox properties (correct Lucid API structure)
      const bbox = shape.boundingBox;
      if (!bbox) return; // Skip if no bounding box
      
      const shapeMinX = bbox.x;
      const shapeMinY = bbox.y;
      const shapeMaxX = bbox.x + bbox.w;
      const shapeMaxY = bbox.y + bbox.h;
      
      minX = Math.min(minX, shapeMinX);
      minY = Math.min(minY, shapeMinY);
      maxX = Math.max(maxX, shapeMaxX);
      maxY = Math.max(maxY, shapeMaxY);
    });
    
    // Add padding to bounds
    const bounds = {
      minX: minX - 50,
      minY: minY - 50,
      maxX: maxX + 50,
      maxY: maxY + 50
    };
    
    this.log('info', `Diagram bounds calculated: X(${bounds.minX} to ${bounds.maxX}), Y(${bounds.minY} to ${bounds.maxY})`);

    // Create Summary shape (positioned left of main diagram at top)
    const summaryShape = this.createSummaryShape(bounds);
    const summaryHeight = summaryShape?.boundingBox?.h || 140;

    // Create AI Summary shape (conditional - only if summary exists)
    const aiSummaryShape = this.createAISummaryShape(bounds, summaryHeight);

    // Create Legend shape - only pass positioning if AI Summary exists
    const legendShape = aiSummaryShape
      ? this.createLegendShape(
          bounds,
          aiSummaryShape.boundingBox.y,
          aiSummaryShape.boundingBox.h
        )
      : this.createLegendShape(bounds);  // No params = use default bottom positioning

    // Combine all shapes: nodes + labels + info boxes (conditionally include AI Summary)
    const infoShapes = aiSummaryShape
      ? [summaryShape, aiSummaryShape, legendShape]
      : [summaryShape, legendShape];

    const allShapes = [...shapes, ...labelShapes, ...infoShapes];

    const document = {
      version: 1,
      pages: [{
        id: 'page1',
        title: 'Flow Diagram',
        shapes: allShapes,
        lines: validLines
      }]
    };

    const aiSummaryInfo = aiSummaryShape ? ', 1 AI summary shape' : '';
    this.log('info', `Document created with ${shapes.length} node shapes, ${labelShapes.length} label shapes, 1 summary shape${aiSummaryInfo}, 1 legend shape (${allShapes.length} total), and ${validLines.length} lines`);
    if (lines.length !== validLines.length) {
      this.log('warn', `Filtered out ${lines.length - validLines.length} invalid lines during creation`);
    }

    return document;
  }

  /**
   * Validate and sanitize Lucid document to ensure API compliance
   */
  validateAndSanitizeLucidDocument(document) {
    // Validate document structure
    if (!document.version || typeof document.version !== 'number') {
      document.version = 1;
    }
    
    if (!document.pages || !Array.isArray(document.pages)) {
      console.error('Document must contain a pages array - this is a critical error!');
      throw new Error('Document must contain a pages array');
    }
    
    // Track validation statistics
    let totalShapesProcessed = 0;
    let validShapes = 0;
    let totalLinesProcessed = 0;
    let validLines = 0;
    let orphanedLinesRemoved = 0;

    // Validate each page
    document.pages.forEach((page, pageIndex) => {
      if (!page.id || typeof page.id !== 'string') {
        page.id = `page${pageIndex + 1}`;
      }
      
      // Build shape ID registry for this page
      const availableShapeIds = new Set();
      
      // Validate shapes first and build ID registry
      if (page.shapes && Array.isArray(page.shapes)) {
        let pageValidShapes = [];
        let pageRemovedShapesCount = 0;
        
        page.shapes.forEach((shape, shapeIndex) => {
          totalShapesProcessed++;
          try {
            this.validateShape(shape);
            pageValidShapes.push(shape);
            availableShapeIds.add(shape.id);
            validShapes++;
          } catch (error) {
            console.error(`Shape validation failed for ${shape.id}:`, error);
            pageRemovedShapesCount++;
          }
        });
        
        if (pageRemovedShapesCount > 0) {
          this.log('warn', `Removed ${pageRemovedShapesCount} invalid shapes`);
          page.shapes = pageValidShapes;
        }
      } else {
        page.shapes = [];
      }
      
      // Validate lines with shape reference checking
      if (page.lines && Array.isArray(page.lines)) {
        let pageValidLines = [];
        let pageRemovedLinesCount = 0;
        let pageOrphanedLinesCount = 0;
        
        page.lines.forEach((line, lineIndex) => {
          totalLinesProcessed++;
          try {
            // Validate basic line structure and cross-reference shape IDs
            this.validateLine(line, availableShapeIds);
            pageValidLines.push(line);
            validLines++;
          } catch (error) {
            // Check if this is specifically an orphaned reference error
            if (error.message.includes('references non-existent shape')) {
              pageOrphanedLinesCount++;
              orphanedLinesRemoved++;
              this.log('warn', `Removed orphaned line ${line.id}: ${error.message}`);
            } else {
              console.error(`Line validation failed for ${line.id}:`, error);
            }
            pageRemovedLinesCount++;
          }
        });
        
        if (pageRemovedLinesCount > 0) {
          this.log('warn', `Removed ${pageRemovedLinesCount} invalid lines (${pageOrphanedLinesCount} orphaned references)`);
          page.lines = pageValidLines;
        }
      } else {
        page.lines = [];
      }
    });
    
    // Log validation summary
    this.log('info', `Document validation: ${validShapes}/${totalShapesProcessed} shapes, ${validLines}/${totalLinesProcessed} lines valid`);
    if (orphanedLinesRemoved > 0) {
      this.log('warn', `Removed ${orphanedLinesRemoved} orphaned line references`);
    }
    
    return document;
  }
  
  /**
   * Validate individual shape object
   */
  validateShape(shape) {
    // Required fields
    if (!shape.id || typeof shape.id !== 'string') {
      console.error(`Shape missing valid ID:`, shape.id);
      throw new Error('Shape must have a string id');
    }
    
    if (!shape.type || typeof shape.type !== 'string') {
      console.error(`Shape ${shape.id} missing valid type:`, shape.type);
      throw new Error('Shape must have a string type');
    }
    
    // Validate boundingBox
    if (!shape.boundingBox) {
      console.error(`Shape ${shape.id} missing boundingBox`);
      throw new Error('Shape must have a boundingBox');
    }
    
    const bbox = shape.boundingBox;
    
    // Fix any invalid bounding box properties
    let fixedProperties = [];
    ['x', 'y', 'w', 'h'].forEach(prop => {
      if (typeof bbox[prop] !== 'number' || isNaN(bbox[prop]) || !isFinite(bbox[prop])) {
        this.log('debug', `Invalid boundingBox.${prop} for shape ${shape.id}: ${bbox[prop]}, auto-fixing`);

        // Provide safe fallback values
        switch (prop) {
          case 'x':
            bbox[prop] = 120; // Safe X position
            break;
          case 'y':
            bbox[prop] = 120; // Safe Y position
            break;
          case 'w':
            bbox[prop] = 120; // Safe width
            break;
          case 'h':
            bbox[prop] = 60;  // Safe height
            break;
          default:
            bbox[prop] = 100; // Safe fallback for any other property
            break;
        }
        fixedProperties.push(prop);
      }
    });
    
    if (fixedProperties.length > 0) {
      this.log('debug', `Auto-fixed bounding box properties [${fixedProperties.join(', ')}] for shape ${shape.id}`);
    }
    
    // Validate style properties if present - make more resilient
    if (shape.style) {
      try {
        this.validateStyleProperties(shape.style, 'shape');
      } catch (styleError) {
        this.log('warn', `Style validation failed for shape ${shape.id}, but keeping shape:`, styleError);
        // Don't throw - just log the warning and continue with default style
        shape.style = {
          fill: { type: 'color', color: '#F0F0F0' },
          stroke: { color: '#666666', width: 2 }
        };
      }
    }
    
    // Validate text field
    if (shape.text && typeof shape.text !== 'string') {
      this.log('debug', `Invalid text field for shape ${shape.id}, converting to string`);
      shape.text = String(shape.text);
    }
  }
  
  /**
   * Validate individual line object
   */
  validateLine(line, availableShapeIds = null) {
    // Basic line structure validation
    if (!line || typeof line !== 'object') {
      throw new Error(`Line is not a valid object: ${typeof line}`);
    }

    if (!line.id || typeof line.id !== 'string' || line.id.trim() === '') {
      throw new Error(`Line missing valid ID: ${line.id}`);
    }

    if (!this.validateLucidId(line.id)) {
      throw new Error(`Line has invalid ID format: ${line.id}`);
    }

    // Validate line type
    const validLineTypes = ['straight', 'elbow', 'curved', 'bezier'];
    if (!line.lineType || !validLineTypes.includes(line.lineType)) {
      line.lineType = 'elbow';
    }

    // Validate endpoints
    if (!line.endpoint1 || !line.endpoint2) {
      throw new Error(`Line ${line.id} missing required endpoints`);
    }

    // Validate endpoint structure
    ['endpoint1', 'endpoint2'].forEach(endpointKey => {
      const endpoint = line[endpointKey];
      
      if (!endpoint.type || endpoint.type !== 'shapeEndpoint') {
        throw new Error(`Line ${line.id} ${endpointKey} must have type 'shapeEndpoint'`);
      }

      if (!endpoint.shapeId || typeof endpoint.shapeId !== 'string') {
        throw new Error(`Line ${line.id} ${endpointKey} missing valid shapeId`);
      }

      // Validate endpoint shapeId references exist (if shape IDs provided)
      if (availableShapeIds && availableShapeIds instanceof Set) {
        if (!availableShapeIds.has(endpoint.shapeId)) {
          throw new Error(`Line ${line.id} ${endpointKey} references non-existent shape: ${endpoint.shapeId}`);
        }
      }

      // Validate endpoint style
      const validStyles = ['none', 'openArrow', 'closedArrow', 'diamond', 'circle'];
      if (!endpoint.style || !validStyles.includes(endpoint.style)) {
        endpoint.style = 'none';
      }
    });

    // Validate stroke properties (line strokes use different format than node styles)
    if (line.stroke) {
      // Line stroke format: { color: "#hexstring", width: integer, style: "solid/dashed/dotted" }
      if (line.stroke.color !== undefined && typeof line.stroke.color !== 'string') {
        throw new Error(`Line ${line.id} stroke.color must be a hex color string, not ${typeof line.stroke.color}`);
      }
      if (line.stroke.width !== undefined && typeof line.stroke.width !== 'number') {
        line.stroke.width = parseInt(line.stroke.width) || 2;
      }
      if (line.stroke.style !== undefined && !['solid', 'dashed', 'dotted'].includes(line.stroke.style)) {
        line.stroke.style = 'solid';
      }
    }

    // Validate custom data if present
    if (line.customData) {
      if (!Array.isArray(line.customData)) {
        throw new Error(`Line ${line.id} customData must be an array`);
      }

      line.customData.forEach((data, index) => {
        if (!data.key || typeof data.key !== 'string') {
          throw new Error(`Line ${line.id} customData[${index}] missing valid key`);
        }
        if (data.value === undefined || data.value === null) {
          throw new Error(`Line ${line.id} customData[${index}] missing value`);
        }
        // Ensure value is a string as required by Lucid API
        if (typeof data.value !== 'string') {
          data.value = String(data.value);
        }
      });
    }

    // Validate joints if present
    if (line.joints && !Array.isArray(line.joints)) {
      delete line.joints;
    }

    // Validate elbow control points if present: must be an array of finite {x, y}.
    // Drop any non-finite points so a bad coordinate can't break the elbow line.
    if (line.elbowControlPoints) {
      if (!Array.isArray(line.elbowControlPoints)) {
        delete line.elbowControlPoints;
      } else {
        line.elbowControlPoints = line.elbowControlPoints.filter(
          (p) => p && isFinite(p.x) && isFinite(p.y)
        );
        if (line.elbowControlPoints.length === 0) {
          delete line.elbowControlPoints;
        }
      }
    }

    // Validate text labels if present
    if (line.text && !Array.isArray(line.text)) {
      delete line.text;
    }

    return true; // Line passed validation
  }
  
  /**
   * Validate style properties for shapes and lines
   */
  validateStyleProperties(style, type) {
    // Numeric properties that must be numbers
    const numericProps = ['opacity', 'borderWidth']; // strokeWidth and fontSize are now strings
    
    numericProps.forEach(prop => {
      if (style[prop] !== undefined) {
        if (typeof style[prop] !== 'number' || isNaN(style[prop]) || !isFinite(style[prop])) {
          this.log('debug', `Invalid ${prop} in ${type} style, fixing...`);
          // Provide reasonable defaults
          switch (prop) {
            case 'opacity':
              style[prop] = 100;
              break;
            case 'borderWidth':
              style[prop] = 1;
              break;
            default:
              style[prop] = 1;
              break;
          }
        }
      }
    });
    
    // Validate Lucid color object format for color properties
    const colorProps = ['fill', 'stroke', 'color', 'backgroundColor', 'borderColor'];
    colorProps.forEach(prop => {
      if (style[prop] !== undefined) {
        // If it's a string (old format), convert to Lucid color object
        if (typeof style[prop] === 'string') {
          const colorValue = style[prop];
          style[prop] = {
            type: 'color',
            color: colorValue
          };
        } 
        // If it's already an object, validate its structure
        else if (typeof style[prop] === 'object' && style[prop] !== null) {
          if (style[prop].type !== 'color' || typeof style[prop].color !== 'string') {
            this.log('debug', `Invalid Lucid color object for ${prop}, automatically converting to proper format`);
            // Try to extract color value if possible
            const colorValue = style[prop].color || style[prop] || '#666666';
            style[prop] = {
              type: 'color',
              color: typeof colorValue === 'string' ? colorValue : '#666666'
            };
          }
        } else {
          this.log('debug', `Invalid ${prop} format, converting to default Lucid color object`);
          style[prop] = {
            type: 'color',
            color: '#666666'
          };
        }
      }
    });
    
    // String properties that must remain as strings (non-color properties)
    const stringProps = ['fontFamily', 'fontWeight', 'textAlign', 'verticalAlign', 'lineType', 'strokeDasharray'];
    stringProps.forEach(prop => {
      if (style[prop] !== undefined && typeof style[prop] !== 'string') {
        this.log('debug', `Invalid ${prop} in ${type} style, converting to string`);
        style[prop] = String(style[prop]);
      }
    });
  }

  /**
   * Create Lucid nodes from Contact Flow actions
   */
  createLucidNodes(actions) {
    const lucidNodes = [];
    const createdNodeIds = new Set();
    
    // Add entry node first
    const entryNode = this.createLucidEntryNode();
    if (entryNode) {
      lucidNodes.push(entryNode);
      createdNodeIds.add(entryNode.id);
    } else {
      this.log('error', 'Failed to create entry node - this will cause export issues');
    }

    // Track node creation statistics
    let successfulNodes = 0;
    let failedNodes = 0;

    // Create nodes for each action
    actions.forEach(action => {
      try {
        const lucidNode = this.createLucidNode(action);
        if (lucidNode && lucidNode.id) {
          lucidNodes.push(lucidNode);
          createdNodeIds.add(lucidNode.id);
          successfulNodes++;
        } else {
          failedNodes++;
          this.log('warn', `Failed to create node for action ${action.Identifier} (${action.Type})`);
        }
      } catch (error) {
        failedNodes++;
        this.log('error', `Exception creating node for action ${action.Identifier} (${action.Type}):`, error);
      }
    });

    // Store tracking data for validation phases
    this.createdNodeIds = createdNodeIds;

    // Log summary
    this.log('info', `Node creation: ${successfulNodes} successful, ${failedNodes} failed, ${lucidNodes.length} total nodes`);

    return lucidNodes;
  }

  /**
   * Creates a summary information shape for the top-left corner
   * Mirrors the summary panel from React Flow viewer
   */
  /**
   * Creates a summary information shape for the top-left corner
   * Mirrors the summary panel from React Flow viewer
   */
  createSummaryShape(bounds) {
    const flowName = this.flowData?.Name || 'Contact Flow';
    const flowType = this.flowData?.Type || 'Unknown';
    const flowDescription = this.flowData?.Description || '';
    const summary = this.getFlowSummary();
    
    // Position at top-left, right edge cleared from the diagram by PANEL_GAP.
    const x = this.getPanelX(bounds);
    const y = bounds.minY;
    const w = this.PANEL_WIDTH;
    const h = flowDescription ? 220 : 140;

    // Build simple HTML content (Lucid has limited HTML support)
    // Use single <p> tag with <br> separators and minimal inline styles
    const htmlStyle = 'text-align: left; padding: 12px; font-size: 9pt; color: #2c3e50;';
    
    // Add extra space after emoji to prevent rendering issues
    let htmlContent = `<b style="font-size: 11pt;">${this.escapeHtml(flowName)}</b><br><br>`;
    htmlContent += `<span style="font-size: 8pt; color: #666;">`;
    htmlContent += `Actions: <b>${summary.actionCount}</b> (+1 Entry)<br>`;
    htmlContent += `Transitions: <b>${summary.transitionCount}</b><br>`;
    htmlContent += `Version: <b>${summary.version || 'N/A'}</b><br>`;
    htmlContent += `Type: <b>${this.escapeHtml(flowType)}</b>`;
    htmlContent += `</span>`;
    
    if (flowDescription) {
      htmlContent += `<br><br><span style="font-size: 8pt; color: #888; font-style: italic;">${this.escapeHtml(flowDescription)}</span>`;
    }
    
    const shape = {
      id: this.generateLucidId('summary-panel'),
      type: 'rectangle',
      boundingBox: {
        x,
        y,
        w,
        h
      },
      text: `<p style="${htmlStyle}">${htmlContent}</p>`,
      style: {
        fill: {
          type: 'color',
          color: '#FFFFFF'
        },
        stroke: {
          color: '#dee2e6',
          width: 1.5
        }
      }
    };
    
    this.log('info', `Created summary shape at (${x}, ${y}) with dimensions ${w}x${h}`);
    return shape;
  }

  /**
   * Creates an AI Flow Summary shape positioned between Summary and Legend
   * Shows AI-generated contact flow summary if available
   */
  createAISummaryShape(bounds, summaryBoxHeight) {
    const aiSummary = this.flowData?.summary;

    // Don't create box if no summary available
    if (!aiSummary) {
      this.log('info', 'No AI summary available, skipping AI summary shape');
      return null;
    }

    // Format summary text (handle array or string)
    const summaryText = Array.isArray(aiSummary)
      ? aiSummary.join('\n')
      : aiSummary;

    const lines = summaryText.split('\n').filter(line => line.trim().length > 0);

    // Heuristic height fallback (used only if DOM measurement is unavailable). Narrower
    // panel ⇒ more wrapping, so use a larger per-line factor than the old 700px box.
    const estimatedVisualLines = lines.length * 1.8;
    const lineHeight = 18;  // Lists need more spacing than plain text
    const fallbackHeight = Math.max(200, estimatedVisualLines * lineHeight + 60 + 40);

    // Position between Summary and Legend; uniform width, right edge cleared from diagram.
    const x = this.getPanelX(bounds);
    const y = bounds.minY + summaryBoxHeight + 20;  // 20px gap below summary
    const w = this.PANEL_WIDTH;

    // Build HTML content using lists (ONLY way to get indentation in Lucid)
    // Lucid API only supports: <ol>, <ul>, <li> for indentation (padding-left NOT supported!)
    const htmlStyle = 'text-align: left; padding: 12px; font-size: 10pt; color: #333; font-family: Consolas, Monaco, Courier, monospace;';

    let htmlContent = `<b style="font-size: 11pt;">Flow Summary</b><br><br>`;

    // Convert summary to HTML list structure with proper markdown-to-HTML conversion
    // Uses convertMarkdownToHtml for **bold** and *italic* support
    const summaryLines = summaryText.split('\n');

    // Collect intro text (lines before the first numbered item) separately
    let introHtml = '';
    let listHtml = '<ol style="margin: 0; padding-left: 20px; font-size: 10pt;">';
    let itemCount = 0;
    let listStarted = false; // Track if we've seen the first numbered item
    let nestingStack = []; // Track open <ul> tags for proper nesting

    for (const line of summaryLines) {
      if (line.trim().length === 0) continue; // Skip empty lines

      // Calculate indentation level (each 2-3 spaces = 1 level)
      const leadingSpaces = line.match(/^(\s*)/)[1].length;
      const indentLevel = Math.floor(leadingSpaces / 2);

      // Check if this is a numbered item (1., 2., 3., etc.)
      if (/^\d+\./.test(line.trim())) {
        listStarted = true; // Mark that the list has started

        // Close all nested lists when returning to main numbered item
        while (nestingStack.length > 0) {
          listHtml += '</li></ul>';
          nestingStack.pop();
        }
        if (itemCount > 0) {
          listHtml += '</li>'; // Close previous main item
        }

        // Start new main item - strip the number prefix since <ol> adds it automatically
        const contentWithoutNumber = line.trim().replace(/^\d+\.\s*/, '');
        const content = this.convertMarkdownToHtml(contentWithoutNumber);
        listHtml += `<li>${content}`;
        itemCount++;

      } else if (line.trim().startsWith('-')) {
        // This is a sub-item - determine its nesting level
        const targetLevel = indentLevel > 0 ? indentLevel : 1;

        // Close deeper levels if moving up
        while (nestingStack.length > targetLevel) {
          listHtml += '</li></ul>';
          nestingStack.pop();
        }

        // Close current item at same level before adding new one
        if (nestingStack.length === targetLevel && nestingStack.length > 0) {
          listHtml += '</li>';
        }

        // Open new levels if going deeper - add <br> before <ul> for line break
        while (nestingStack.length < targetLevel) {
          listHtml += '<br><ul style="margin: 5px 0 0 0; padding-left: 20px;">';
          nestingStack.push(nestingStack.length + 1);
        }

        // Add sub-item - strip the dash prefix since <ul> adds bullet automatically
        const contentWithoutDash = line.trim().replace(/^-\s*/, '');
        const content = this.convertMarkdownToHtml(contentWithoutDash);
        listHtml += `<li>${content}`;

      } else if (line.trim().length > 0) {
        // Text that's not a numbered item or bullet point
        const content = this.convertMarkdownToHtml(line.trim());

        if (!listStarted) {
          // This is intro text BEFORE the list - add it separately
          if (introHtml.length > 0) {
            introHtml += '<br>';
          }
          introHtml += content;
        } else {
          // Continuation of previous item (wrapped text)
          listHtml += ` ${content}`;
        }
      }
    }

    // Close all remaining open tags
    while (nestingStack.length > 0) {
      listHtml += '</li></ul>';
      nestingStack.pop();
    }
    if (itemCount > 0) {
      listHtml += '</li>';
    }
    listHtml += '</ol>';

    // Add intro text before the list if present
    if (introHtml.length > 0) {
      htmlContent += introHtml + '<br><br>';
    }

    htmlContent += listHtml;

    // Measure the finished HTML at the panel width for an accurate height (so the box
    // isn't too short/tall); fall back to the line-count estimate when no DOM.
    const measured = measureLucidHtml(htmlContent, htmlStyle, { maxWidth: w });
    const h = measured && measured.height > 0
      ? Math.max(200, measured.height + 24)
      : fallbackHeight;

    const shape = {
      id: this.generateLucidId('ai-summary-panel'),
      type: 'rectangle',
      boundingBox: { x, y, w, h },
      text: `<p style="${htmlStyle}">${htmlContent}</p>`,
      style: {
        fill: { type: 'color', color: '#FFFFFF' },
        stroke: { color: '#dee2e6', width: 1.5 },
        rounding: 4
      }
    };

    this.log('info', `Created AI summary shape at (${x}, ${y}) with dimensions ${w}x${h} for ${lines.length} lines using HTML lists for indentation`);
    return shape;
  }

  /**
   * Creates a legend shape for the bottom-left corner
   * Mirrors the legend panel from React Flow viewer
   */
  createLegendShape(bounds, previousY, previousHeight) {
    // Position at bottom-left; uniform width, right edge cleared from the diagram.
    const x = this.getPanelX(bounds);
    const legendHeight = 380;

    // Calculate Y position: if AI Summary exists, position below it; otherwise use bottom of diagram
    const y = previousY !== undefined && previousHeight !== undefined
      ? previousY + previousHeight + 20  // Position 20px below AI Summary
      : bounds.maxY - legendHeight;       // Fallback to bottom if no AI Summary
    const w = this.PANEL_WIDTH;
    
    // Build legend content using Unicode symbols (Lucid has limited HTML support)
    // Use single <p> tag with <br> separators
    const htmlStyle = 'text-align: left; padding: 10px; font-size: 8pt; color: #333;';
    
    let htmlContent = `<b style="font-size: 10pt;">Node Types</b><br><br>`;
    
    // Node types with Unicode symbols
    htmlContent += `<span style="color: #9e9e9e;">\u25cf</span> Entry Point<br>`;
    htmlContent += `<span style="color: #4caf50;">\u25cf</span> Start/End Actions<br>`;
    htmlContent += `<span style="color: #2196F3;">\u25a0</span> Messages/Communication<br>`;
    htmlContent += `<span style="color: #9C27B0;">\u25a0</span> Input Collection<br>`;
    htmlContent += `<span style="color: #FF9800;">\u25a0</span> Integration/Lambda<br>`;
    htmlContent += `<span style="color: #4CAF50;">\u25a0</span> Routing/Queues<br>`;
    htmlContent += `<span style="color: #ff9800;">\u25c6</span> Decision/Compare<br>`;
    htmlContent += `<span style="color: #607D8B;">\u25a0</span> Data/Attributes<br>`;
    
    htmlContent += `<br><b style="font-size: 10pt;">Edge Types</b><br><br>`;
    
    // Edge types with visual indicators
    htmlContent += `<span style="color: #4CAF50;">\u2501\u2501\u2501</span> Success Path<br>`;
    htmlContent += `<span style="color: #f44336;">\u2504\u2504\u2504</span> Error Path<br>`;
    htmlContent += `<span style="color: #2196f3;">\u2501\u2501\u2501</span> Condition Path`;
    
    const shape = {
      id: this.generateLucidId('legend-panel'),
      type: 'rectangle',
      boundingBox: {
        x,
        y,
        w,
        h: legendHeight
      },
      text: `<p style="${htmlStyle}">${htmlContent}</p>`,
      style: {
        fill: {
          type: 'color',
          color: '#FFFFFF'
        },
        stroke: {
          color: '#dee2e6',
          width: 1.5
        }
      }
    };
    
    this.log('info', `Created legend shape at (${x}, ${y}) with dimensions ${w}x${legendHeight}`);
    return shape;
  }

  /**
   * Helper method to escape HTML in text content
   */
  escapeHtml(text) {
    if (!text) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  /**
   * Converts markdown formatting to HTML for Lucid Charts
   * Handles: **bold** → <b>, *italic* → <i>
   * Also escapes HTML special characters that are not part of markdown
   * Uses &nbsp; around formatted text to prevent Lucid from collapsing spaces
   */
  convertMarkdownToHtml(text) {
    if (!text) return '';

    let result = String(text);

    // Step 1: Escape HTML special characters first
    result = result
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    // Step 2: Convert **bold** to <b>bold</b> (must come before single *)
    // Use &nbsp; to preserve spaces around formatted text (Lucid collapses regular spaces)
    result = result.replace(/(\s?)\*\*([^*]+)\*\*(\s?)/g, (match, before, content, after) => {
      const prefix = before ? '&nbsp;' : '';
      const suffix = after ? '&nbsp;' : '';
      return `${prefix}<b>${content}</b>${suffix}`;
    });

    // Step 3: Convert *italic* to <i>italic</i> (single asterisks only)
    // Use &nbsp; to preserve spaces around formatted text (Lucid collapses regular spaces)
    result = result.replace(/(\s?)\*([^*]+)\*(\s?)/g, (match, before, content, after) => {
      const prefix = before ? '&nbsp;' : '';
      const suffix = after ? '&nbsp;' : '';
      return `${prefix}<i>${content}</i>${suffix}`;
    });

    return result;
  }

  /**
   * Create entry node in Lucid format
   */
  createLucidEntryNode() {
    // Placeholder position; optimizeLayoutPositions (dagre) repositions it afterward.
    const position = this.getLucidCoordinates({ x: 0, y: 0 });

    // Get HTML inline style for entry node text
    const htmlStyle = this.getProfessionalTextStyle('terminator', 'terminator');

    return {
      id: 'entry-node',
      type: 'terminator', // Changed from 'circle' to 'terminator' for proper flowchart shape
      boundingBox: {
        x: position.x,
        y: position.y,
        w: 100, // "Entry" text fits comfortably; fixed terminator size
        h: 60
      },
      style: this.getEntryNodeStyle(),
      text: `<p style="${htmlStyle}">Entry</p>`
    };
  }

  /**
   * Create Lucid node from Contact Flow action
   */
  createLucidNode(action) {
    const nodeType = ContactFlowParsingUtils.getNodeType(action);
    const category = ContactFlowParsingUtils.getBlockCategory(action);
    const displayInfo = ContactFlowParsingUtils.getDisplayInfo(action, this.exportedData, this.instanceId);
    const actionLabel = ContactFlowParsingUtils.getActionLabel(action, this.exportedData, this.instanceId);
    
    // Determine Lucid shape type
    const lucidShapeType = this.getLucidShapeType(nodeType, action.Type);

    // Initial position; optimizeLayoutPositions (dagre) repositions every node afterward
    // using the measured dimensions below, so this is just a placeholder seed.
    const position = this.getActionPosition(action.Identifier) || this.calculateNodePosition(action);
    const lucidPosition = this.getLucidCoordinates(position);

    // Generate rich HTML content with formatting (already HTML-escaped internally)
    const richContent = this.generateRichTextContent(action, actionLabel, displayInfo);

    // Get HTML inline style for text
    const htmlStyle = this.getProfessionalTextStyle(category, lucidShapeType);

    // Escape the action label and type for HTML (user-provided text)
    const escapeHtml = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };

    // Format action type for display (e.g., "MessageParticipant" -> "Message Participant")
    const formattedActionType = action.Type.replace(/([A-Z])/g, ' $1').trim();

    // Build complete HTML text content with proper structure
    // Title (bold, larger) + Type (small, gray) + Rich Content
    let htmlContent = `<b style="font-size: 9pt;">${escapeHtml(actionLabel)}</b>`;
    htmlContent += `<br><span style="font-size: 7pt; color: #666;">${escapeHtml(formattedActionType)}</span>`;

    // Add rich content if available (already contains proper HTML and escaping)
    if (richContent && richContent.trim().length > 0) {
      htmlContent += '<br><br>' + richContent;
    }

    // Hard-wrap the content to a fixed per-shape character budget by inserting explicit
    // <br> breaks. This makes the visual line count DETERMINISTIC and identical in both our
    // measurement and Lucid's renderer (Lucid soft-wraps long lines differently than the
    // browser, which was the root cause of residual text clipping). The same wrapped HTML is
    // used for both the shape text and the measurement below, so Lucid renders what we sized.
    htmlContent = this.hardWrapHtml(htmlContent, this.getWrapCharsForShape(lucidShapeType));

    // Size the shape to its OWN (pre-wrapped) rendered HTML so text never overflows.
    // Falls back to the char-width heuristic when DOM measurement is unavailable.
    const dimensions = this.measureNodeDimensions(
      htmlContent, htmlStyle, lucidShapeType, actionLabel, displayInfo
    );

    const lucidNode = {
      id: this.generateLucidId('nd-', action.Identifier),
      type: lucidShapeType,
      boundingBox: {
        x: lucidPosition.x,
        y: lucidPosition.y,
        w: dimensions.width,
        h: dimensions.height
      },
      style: this.getProfessionalNodeStyle(category, nodeType, action.Type),
      text: `<p style="${htmlStyle}">${htmlContent}</p>`,
      // Add sideWidth property for predefinedProcess shapes (required by Lucid API)
      ...(lucidShapeType === 'predefinedProcess' && { sideWidth: 0.1 }),
      // customData is an array of {key, value} per the Lucid API. Keep only the small
      // identifying fields — the full displayInfo JSON was embedded on every shape and
      // bloated document.json toward the 1.5MB Standard Import limit for little benefit.
      customData: [{
        key: "actionType",
        value: String(action.Type)
      }, {
        key: "category",
        value: String(category)
      }, {
        key: "nodeType",
        value: String(nodeType)
      }, {
        key: "originalId",
        value: String(action.Identifier)
      }]
    };
    
    // Validate the node before returning
    if (!lucidNode.id || !lucidNode.type || !lucidNode.boundingBox) {
      console.error('Invalid node created - missing required fields:', lucidNode);
      return null;
    }
    
    // Validate coordinates are valid numbers
    const bbox = lucidNode.boundingBox;
    if (isNaN(bbox.x) || isNaN(bbox.y) || isNaN(bbox.w) || isNaN(bbox.h) ||
        !isFinite(bbox.x) || !isFinite(bbox.y) || !isFinite(bbox.w) || !isFinite(bbox.h)) {
      console.error('Invalid bounding box coordinates:', bbox);
      return null;
    }
    
    return lucidNode;
  }


  /**
   * Get Lucid shape type from node type and action type
   */
  getLucidShapeType(nodeType, actionType = null) {
    switch (nodeType) {
      case 'entryNode':
        return 'terminator'; // Use flowchart terminator for entry
      case 'endNode':
        return 'terminator'; // Use flowchart terminator for end
      case 'decisionNode':
        return 'decision'; // Use flowchart decision diamond
      case 'processNode':
      default:
        // Use more specific flowchart shapes based on action type
        if (actionType) {
          switch (actionType) {
            case 'MessageParticipant':
            case 'MessageParticipantIteratively':
              return 'document'; // Flowchart document shape
            case 'GetParticipantInput':
            case 'ConnectParticipantWithLexBot':
              return 'document'; // Use document instead of manualInput
            case 'InvokeLambdaFunction':
              return 'predefinedProcess'; // Flowchart predefined process
            case 'TransferContactToQueue':
            case 'TransferToFlow':
            case 'TransferParticipantToThirdParty':
              return 'process'; // Use standard process shape
            case 'UpdateContactAttributes':
            case 'UpdateContactTargetQueue':
              return 'process'; // Use standard process shape
            case 'Wait':
              return 'process'; // Use standard process shape
            default:
              return 'process'; // Default flowchart process shape
          }
        }
        return 'process';
    }
  }

  /**
   * Character budget per shape type for hard-wrapping node text. Sets the line length /
   * aspect ratio; the box is then sized to fit the wrapped lines. Fewer chars → narrower,
   * taller boxes (good for diamonds); more chars → wider, shorter boxes.
   */
  getWrapCharsForShape(shapeType) {
    const CHARS_BY_SHAPE = {
      decision: 20,        // diamond — short lines so the 2× box doesn't get huge
      predefinedProcess: 32, // Lambda — narrower usable width (side bars)
      document: 34,
      manualInput: 34,
      terminator: 26,
      circle: 14
    };
    return CHARS_BY_SHAPE[shapeType] || 40; // process / default
  }

  /**
   * Greedy, tag-aware hard wrap: insert explicit <br> into `html` so no visible line exceeds
   * `maxChars`. Tags (<b>, <span …>, </…>) don't count toward the line length; HTML entities
   * (&amp; etc.) count as one char; the counter resets on existing <br>/<p> boundaries. This
   * makes the rendered line count DETERMINISTIC (identical in our measurement and in Lucid),
   * which is what guarantees the box height is correct and text never clips.
   */
  hardWrapHtml(html, maxChars) {
    if (!html || maxChars <= 0) return html;
    const out = [];
    let lineLen = 0;        // visible chars on the current line
    let lastSpaceIdx = -1;  // index in `out` of the last breakable space on this line
    const hardLimit = Math.ceil(maxChars * 1.4); // force-break a spaceless run past this

    for (let i = 0; i < html.length; ) {
      const ch = html[i];

      if (ch === '<') {
        // Emit the whole tag verbatim; tags don't count toward line length.
        const end = html.indexOf('>', i);
        const tag = end === -1 ? html.slice(i) : html.slice(i, end + 1);
        out.push(tag);
        i += tag.length;
        if (/^<br|^<\/?p\b/i.test(tag)) { lineLen = 0; lastSpaceIdx = -1; } // line boundary
        continue;
      }

      if (ch === '&') {
        // Treat a whole entity as one visible character.
        const semi = html.indexOf(';', i);
        if (semi !== -1 && semi - i <= 10) {
          out.push(html.slice(i, semi + 1));
          i = semi + 1;
          lineLen++;
        } else {
          out.push(ch); i++; lineLen++;
        }
      } else {
        if (ch === ' ') lastSpaceIdx = out.length; // record where we could break
        out.push(ch); i++; lineLen++;
      }

      if (lineLen > maxChars && lastSpaceIdx !== -1) {
        // Greedy word wrap: turn the last space into a line break.
        const charsAfterSpace = out.length - 1 - lastSpaceIdx;
        out[lastSpaceIdx] = '<br>';
        lineLen = charsAfterSpace;
        lastSpaceIdx = -1;
      } else if (lineLen > hardLimit) {
        // No space to break on (e.g. a long ARN/URI): hard-break before the last char.
        out.splice(out.length - 1, 0, '<br>');
        lineLen = 1;
        lastSpaceIdx = -1;
      }
    }
    return out.join('');
  }

  /**
   * Count the rendered lines of a node's (already hard-wrapped) HTML: every explicit <br>
   * starts a new line, so the line count is (number of <br>) + 1. A <br><br> correctly
   * yields a blank line. This is exact because hardWrapHtml inserted real <br> for all wraps.
   */
  countTextLines(html) {
    if (!html) return 1;
    const breaks = (html.match(/<br\s*\/?>/gi) || []).length;
    return breaks + 1;
  }

  /**
   * Size a node from its (already hard-wrapped) HTML.
   * - WIDTH comes from measuring the widest hard-wrapped line (reliable; no soft-wrapping).
   * - HEIGHT is computed from the DETERMINISTIC line count × a generous per-line height,
   *   NOT from the browser's measured pixel height. Lucid renders with a taller line-height
   *   than the browser (it ignores our CSS line-height on import), so a measured pixel height
   *   under-sizes the box and clips the last line. Line-count × generous LINE_PX is independent
   *   of Lucid's line model and guarantees the text fits.
   * Falls back to the char-width heuristic (calculateNodeDimensions) when there's no DOM.
   */
  measureNodeDimensions(htmlContent, htmlStyle, shapeType, label, displayInfo) {
    // Measure with a large max width so measurement does NOT add its own wrapping — the text
    // is already hard-wrapped, so `measured.width` is the true widest line.
    const measured = measureLucidHtml(htmlContent, htmlStyle, { maxWidth: 2000 });
    if (!measured || !(measured.width > 0)) {
      // No DOM (or empty measurement) — fall back to the heuristic estimator.
      return this.calculateNodeDimensions(label, displayInfo, shapeType);
    }

    // Generous per-line height (px) — must exceed Lucid's line-height for our ≤9pt text
    // (Lucid renders taller than the browser). 22 covers a 9pt title line at Lucid's scale.
    const LINE_PX = 22;
    const textHeight = this.countTextLines(htmlContent) * LINE_PX;

    const box = this.computeShapeBox({ width: measured.width, height: textHeight }, shapeType);

    // Clamp to sane bounds (generous max so tall content isn't truncated) and grid-snap.
    const snap = (v) => Math.ceil(v / this.LUCID_GRID_SIZE) * this.LUCID_GRID_SIZE;
    const width = snap(Math.max(this.NODE_MIN_WIDTH, Math.min(box.width, 720)));
    const height = snap(Math.max(this.NODE_MIN_HEIGHT, Math.min(box.height, 940)));
    return { width, height };
  }

  /**
   * Turn a measured text block { width, height } into a bounding box that actually CONTAINS
   * the text for a given Lucid shape type. Each shape exposes a different usable text area —
   * static padding can't model that — so we invert each shape's geometry:
   *   - decision (diamond): text fits only the inscribed rectangle (~half each axis) → 2×
   *   - predefinedProcess (Lambda): vertical side bars cost `sideWidth` (0.1) on each side
   *   - document / manualInput: wavy base eats vertical room
   *   - terminator (stadium): rounded end-caps cost ~height of horizontal room
   *   - process / rectangle (default): symmetric inset
   * Biased to never clip via FONT_SAFETY factors (covers residual font/engine differences).
   */
  computeShapeBox(measured, shapeType) {
    // Lucid renders our pt-sized text ~1.3-1.4× LARGER than the browser does (uniform on both
    // axes), so the browser-measured width under-sizes the box. Scale the measured width up by
    // the same Lucid-vs-browser ratio that the generous LINE_PX already bakes into height, so
    // both axes are treated consistently. This is the single width tuning lever.
    const LUCID_RENDER_SCALE = 1.4; // browser px → Lucid px (widen so text fits horizontally)
    const INSET_X = 16;         // text-to-border breathing room, horizontal
    const INSET_Y = 16;         // vertical buffer (also covers Lucid's internal text padding)
    const PREDEFINED_SIDE = 0.1; // matches sideWidth on predefinedProcess shapes
    const DOC_WAVE = 22;        // vertical room lost to a document's wavy bottom

    // `measured.height` is already a generous line-count × LINE_PX estimate (deliberate
    // over-estimate), so it is used as-is; only width needs the render-scale correction.
    const tW = measured.width * LUCID_RENDER_SCALE;
    const tH = measured.height;

    let width, height;
    switch (shapeType) {
      case 'decision': // diamond — inscribed rectangle is ~half the box on each axis
        width = 2 * (tW + INSET_X);
        height = 2 * (tH + INSET_Y);
        break;
      case 'predefinedProcess': { // Lambda — usable width loses the two side bars
        const usable = 1 - 2 * PREDEFINED_SIDE; // 0.8
        width = tW / usable + 8;
        height = tH + 2 * INSET_Y;
        break;
      }
      case 'document':
      case 'manualInput':
        width = tW + 2 * INSET_X;
        height = tH + INSET_Y + DOC_WAVE;
        break;
      case 'terminator': { // stadium — rounded caps cost ~height of horizontal room each side
        height = tH + 2 * INSET_Y;
        width = tW + height + 8;
        break;
      }
      case 'circle': { // keep entry/round shapes compact and near-square
        const d = Math.max(80, tW + 2 * INSET_X, tH + 2 * INSET_Y);
        width = d;
        height = Math.min(d, 100);
        break;
      }
      default: // process / rectangle
        width = tW + 2 * INSET_X;
        height = tH + 2 * INSET_Y;
    }
    return { width, height };
  }

  /**
   * Calculate node dimensions based on content and shape type
   */
  calculateNodeDimensions(label, displayInfo, shapeType) {
    // Base dimensions vary by shape type
    let baseWidth = this.NODE_MIN_WIDTH;
    let baseHeight = this.NODE_MIN_HEIGHT;
    
    // Adjust base dimensions for different shape types
    switch (shapeType) {
      case 'circle':
        baseWidth = baseHeight = 80;
        break;
      case 'decision':
        baseWidth = 160;
        baseHeight = 100;
        break;
      case 'document':
      case 'manualInput':
        baseWidth = 180;
        baseHeight = 100;
        break;
      case 'predefinedProcess':
        baseWidth = 200;
        baseHeight = 80;
        break;
      case 'delay':
        baseWidth = 140;
        baseHeight = 100;
        break;
      default:
        baseWidth = this.NODE_MIN_WIDTH;
        baseHeight = this.NODE_MIN_HEIGHT;
    }
    
    let width = baseWidth;
    let height = baseHeight;
    
    // For non-circle shapes, calculate width and height based on actual content
    if (shapeType !== 'circle') {
      // Font metrics for accurate width calculations
      // These are based on Inter font rendering in Lucid Charts
      const CHAR_WIDTH_9PT_BOLD = 10;   // Title text
      const CHAR_WIDTH_8PT_BOLD = 9;    // Bold labels like "Metric:"
      const CHAR_WIDTH_8PT = 8.5;       // Regular content
      const CHAR_WIDTH_7PT = 7;         // Small metadata text
      
      const LINE_HEIGHT = 16;           // Matches line-height: 1.4 for 8pt font
      const HORIZONTAL_PADDING = 60;    // 30px on each side for safety
      const MAX_WIDTH = 500;            // Maximum reasonable width
      
      // Track the widest line to determine node width
      let maxLineWidth = 0;
      
      // Calculate title width (9pt bold)
      const titleWidth = label.length * CHAR_WIDTH_9PT_BOLD;
      maxLineWidth = Math.max(maxLineWidth, titleWidth);
      
      // Calculate action type width (7pt, formatted like "Message Participant")
      const formattedActionType = label.replace(/([A-Z])/g, ' $1').trim();
      const actionTypeWidth = formattedActionType.length * CHAR_WIDTH_7PT;
      maxLineWidth = Math.max(maxLineWidth, actionTypeWidth);
      
      // Now calculate widths for all content items
      // We need to check each displayInfo property and calculate its rendered width
      
      // Messages and prompts
      if (displayInfo.displayMessage) {
        // "SSML:" label + content or just content
        if (displayInfo.contentType === 'text') {
          maxLineWidth = Math.max(maxLineWidth, displayInfo.displayMessage.length * CHAR_WIDTH_8PT);
        }
      }
      
      if (displayInfo.displayPrompt) {
        if (displayInfo.contentType === 'text') {
          maxLineWidth = Math.max(maxLineWidth, displayInfo.displayPrompt.length * CHAR_WIDTH_8PT);
        }
      }
      
      if (displayInfo.displaySSML) {
        // "SSML:" label (8pt bold) + space
        const ssmllabelWidth = 5 * CHAR_WIDTH_8PT_BOLD;
        maxLineWidth = Math.max(maxLineWidth, ssmllabelWidth + displayInfo.displaySSML.length * CHAR_WIDTH_8PT);
      }
      
      if (displayInfo.promptName) {
        // "Prompt:" label (8pt bold)
        const promptLabelWidth = 7 * CHAR_WIDTH_8PT_BOLD;
        maxLineWidth = Math.max(maxLineWidth, promptLabelWidth + displayInfo.promptName.length * CHAR_WIDTH_8PT);
      }
      
      if (displayInfo.mediaUri) {
        // "Media:" label
        const mediaLabelWidth = 6 * CHAR_WIDTH_8PT_BOLD;
        maxLineWidth = Math.max(maxLineWidth, mediaLabelWidth + displayInfo.mediaUri.length * CHAR_WIDTH_8PT);
      }
      
      // Timeout (small gray text)
      if (displayInfo.timeout !== undefined) {
        const timeoutText = `Timeout: ${displayInfo.timeout}s`;
        maxLineWidth = Math.max(maxLineWidth, timeoutText.length * CHAR_WIDTH_7PT);
      }
      
      // Lambda function name
      if (displayInfo.functionName) {
        maxLineWidth = Math.max(maxLineWidth, displayInfo.functionName.length * CHAR_WIDTH_8PT);
        
        // Lambda invocation attributes
        if (displayInfo.lambdaInvocationAttributes) {
          const headerText = `Invocation Attributes (${displayInfo.attributeCount}):`;
          maxLineWidth = Math.max(maxLineWidth, headerText.length * CHAR_WIDTH_7PT);
          
          // Check each attribute line
          Object.entries(displayInfo.lambdaInvocationAttributes).slice(0, 3).forEach(([key, value]) => {
            //const attrLine = `${key}: ${String(value)}`;
            // Key is bold, value is regular
            const attrWidth = key.length * CHAR_WIDTH_8PT_BOLD + 2 * CHAR_WIDTH_8PT + String(value).length * CHAR_WIDTH_8PT;
            maxLineWidth = Math.max(maxLineWidth, attrWidth);
          });
        }
      }
      
      // Contact attributes
      if (displayInfo.attributes && displayInfo.attributeCount > 0) {
        Object.entries(displayInfo.attributes).slice(0, 3).forEach(([key, value]) => {
          //const attrLine = `${key}: ${String(value)}`;
          const attrWidth = key.length * CHAR_WIDTH_8PT_BOLD + 2 * CHAR_WIDTH_8PT + String(value).length * CHAR_WIDTH_8PT;
          maxLineWidth = Math.max(maxLineWidth, attrWidth);
        });
      }
      
      // Hours of operation
      if (displayInfo.hoursName && displayInfo.hoursName !== 'Check Hours') {
        const hoursLabelWidth = 6 * CHAR_WIDTH_8PT_BOLD; // "Hours:"
        maxLineWidth = Math.max(maxLineWidth, hoursLabelWidth + displayInfo.hoursName.length * CHAR_WIDTH_8PT);
      }
      
      // Comparison value
      if (displayInfo.comparisonValue) {
        maxLineWidth = Math.max(maxLineWidth, displayInfo.comparisonValue.length * CHAR_WIDTH_8PT);
      }
      
      // Metric data - can have long combined lines
      if (displayInfo.metricType) {
        const metricLabel = `Metric: ${displayInfo.metricType}`;
        maxLineWidth = Math.max(maxLineWidth, metricLabel.length * CHAR_WIDTH_8PT);
        
        if (displayInfo.queueName) {
          const queueLine = `Queue: ${displayInfo.queueName}`;
          maxLineWidth = Math.max(maxLineWidth, queueLine.length * CHAR_WIDTH_8PT);
        }
        
        if (displayInfo.agentName) {
          const agentLine = `Agent: ${displayInfo.agentName}`;
          maxLineWidth = Math.max(maxLineWidth, agentLine.length * CHAR_WIDTH_8PT);
        }
        
        if (displayInfo.statistic && displayInfo.threshold && displayInfo.operator) {
          const conditionLine = `${displayInfo.statistic} ${displayInfo.operator} ${displayInfo.threshold}`;
          maxLineWidth = Math.max(maxLineWidth, conditionLine.length * CHAR_WIDTH_8PT);
        }
      }
      
      // Phone numbers
      if (displayInfo.phoneNumber) {
        maxLineWidth = Math.max(maxLineWidth, displayInfo.phoneNumber.length * CHAR_WIDTH_8PT);
      }
      
      // Target queue
      if (displayInfo.targetQueue) {
        const queueLine = `Queue: ${displayInfo.targetQueue}`;
        maxLineWidth = Math.max(maxLineWidth, queueLine.length * CHAR_WIDTH_8PT);
      }
      
      // Routing priority/time adjustment
      if (displayInfo.routingType === 'priority' && displayInfo.queuePriority !== undefined) {
        const priorityLabel = 'Queue Priority:';
        maxLineWidth = Math.max(maxLineWidth, priorityLabel.length * CHAR_WIDTH_7PT);
        maxLineWidth = Math.max(maxLineWidth, String(displayInfo.queuePriority).length * CHAR_WIDTH_8PT);
      } else if (displayInfo.routingType === 'timeAdjustment' && displayInfo.queueTimeAdjustment !== undefined) {
        const timeLabel = 'Time Adjustment:';
        maxLineWidth = Math.max(maxLineWidth, timeLabel.length * CHAR_WIDTH_7PT);
        const timeValue = `${displayInfo.queueTimeAdjustment}s`;
        maxLineWidth = Math.max(maxLineWidth, timeValue.length * CHAR_WIDTH_8PT);
      }
      
      // View ID
      if (displayInfo.viewId) {
        const viewLabel = 'View ID:';
        maxLineWidth = Math.max(maxLineWidth, viewLabel.length * CHAR_WIDTH_7PT);
        maxLineWidth = Math.max(maxLineWidth, displayInfo.viewId.length * CHAR_WIDTH_8PT);
      }
      
      // Wait duration
      if (displayInfo.timeoutSeconds !== undefined) {
        const waitLabel = 'Wait Duration:';
        maxLineWidth = Math.max(maxLineWidth, waitLabel.length * CHAR_WIDTH_7PT);
        const waitValue = `${displayInfo.timeoutSeconds}s`;
        maxLineWidth = Math.max(maxLineWidth, waitValue.length * CHAR_WIDTH_8PT);
      }
      
      // Loop count
      if (displayInfo.loopCount !== undefined) {
        const loopLabel = 'Loop Count:';
        maxLineWidth = Math.max(maxLineWidth, loopLabel.length * CHAR_WIDTH_7PT);
        const loopValue = `${displayInfo.loopCount}x`;
        maxLineWidth = Math.max(maxLineWidth, loopValue.length * CHAR_WIDTH_8PT);
      }
      
      // Iterative messages
      if (displayInfo.messages && displayInfo.messageCount > 0) {
        const headerText = `Messages (${displayInfo.messageCount}):`;
        maxLineWidth = Math.max(maxLineWidth, headerText.length * CHAR_WIDTH_7PT);
        
        displayInfo.messages.slice(0, 3).forEach(message => {
          const messagePrefix = `${message.index}. `;
          const prefixWidth = messagePrefix.length * CHAR_WIDTH_8PT;
          const contentWidth = message.content.length * CHAR_WIDTH_8PT;
          maxLineWidth = Math.max(maxLineWidth, prefixWidth + contentWidth);
        });
      }
      
      // TTS Voice settings
      if (displayInfo.textToSpeechVoice) {
        const voiceLine = `Voice: ${displayInfo.textToSpeechVoice}`;
        maxLineWidth = Math.max(maxLineWidth, voiceLine.length * CHAR_WIDTH_8PT);
      }
      if (displayInfo.textToSpeechEngine) {
        const engineLine = `Engine: ${displayInfo.textToSpeechEngine}`;
        maxLineWidth = Math.max(maxLineWidth, engineLine.length * CHAR_WIDTH_8PT);
      }
      if (displayInfo.textToSpeechStyle) {
        const styleLine = `Style: ${displayInfo.textToSpeechStyle}`;
        maxLineWidth = Math.max(maxLineWidth, styleLine.length * CHAR_WIDTH_8PT);
      }
      
      // Participant state
      if (displayInfo.displayState) {
        const actionLabel = 'Action:';
        maxLineWidth = Math.max(maxLineWidth, actionLabel.length * CHAR_WIDTH_7PT);
        maxLineWidth = Math.max(maxLineWidth, displayInfo.displayState.length * CHAR_WIDTH_8PT);
      }
      
      // Flow module
      if (displayInfo.flowModuleName) {
        maxLineWidth = Math.max(maxLineWidth, displayInfo.flowModuleName.length * CHAR_WIDTH_8PT);
      }
      
      // Transfer to flow
      if (displayInfo.targetFlowName) {
        maxLineWidth = Math.max(maxLineWidth, displayInfo.targetFlowName.length * CHAR_WIDTH_8PT);
      }
      
      // Queue name (UpdateContactTargetQueue)
      if (displayInfo.queueName && !displayInfo.metricType) {
        maxLineWidth = Math.max(maxLineWidth, displayInfo.queueName.length * CHAR_WIDTH_8PT);
      }
      
      // Calculate final width with padding
      width = maxLineWidth + HORIZONTAL_PADDING;
      
      // Apply constraints
      width = Math.max(width, baseWidth);
      width = Math.min(width, MAX_WIDTH);
      
      // Now calculate height based on content wrapping at this width
      const usableWidth = width - HORIZONTAL_PADDING;
      let contentLines = 2; // Title + Action Type
      let hasContent = false;
      
      // Recalculate line counts with wrapping based on final width
      // For simplicity, we'll use average character width for wrapping calculations
      const avgCharsPerLine = Math.floor(usableWidth / CHAR_WIDTH_8PT);
      
      // Messages and prompts with wrapping
      if (displayInfo.displayMessage) {
        hasContent = true;
        contentLines += Math.ceil(displayInfo.displayMessage.length / avgCharsPerLine);
      }
      
      if (displayInfo.displayPrompt) {
        hasContent = true;
        contentLines += Math.ceil(displayInfo.displayPrompt.length / avgCharsPerLine);
      }
      
      if (displayInfo.displaySSML) {
        hasContent = true;
        contentLines += 1; // Label
        contentLines += Math.ceil(displayInfo.displaySSML.length / avgCharsPerLine);
      }
      
      if (displayInfo.promptName) {
        hasContent = true;
        contentLines += 1; // Label
        contentLines += Math.ceil(displayInfo.promptName.length / avgCharsPerLine);
      }
      
      if (displayInfo.mediaUri) {
        hasContent = true;
        contentLines += 1; // Label
        contentLines += Math.ceil(displayInfo.mediaUri.length / avgCharsPerLine);
      }
      
      // All other content (add lines without wrapping for simplicity)
      if (displayInfo.timeout !== undefined) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.storeInput) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.functionName) {
        hasContent = true;
        contentLines += 1;
        if (displayInfo.lambdaInvocationAttributes && displayInfo.attributeCount > 0) {
          contentLines += 1; // Header
          const attrsToShow = Math.min(3, displayInfo.attributeCount);
          contentLines += attrsToShow;
          if (displayInfo.attributeCount > 3) {
            contentLines += 1; // "+N more..."
          }
        }
      }
      
      if (displayInfo.attributeCount > 0 && displayInfo.attributes) {
        hasContent = true;
        const attrsToShow = Math.min(3, displayInfo.attributeCount);
        contentLines += attrsToShow;
        if (displayInfo.attributeCount > 3) {
          contentLines += 1;
        }
      }
      
      if (displayInfo.lexBotInfo) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.hoursName && displayInfo.hoursName !== 'Check Hours') {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.comparisonValue) {
        hasContent = true;
        contentLines += Math.ceil(displayInfo.comparisonValue.length / avgCharsPerLine);
      }
      
      if (displayInfo.metricType) {
        hasContent = true;
        contentLines += 1;
        if (displayInfo.queueName) contentLines += 1;
        if (displayInfo.agentName) contentLines += 1;
        if (displayInfo.statistic && displayInfo.threshold) contentLines += 1;
      }
      
      if (displayInfo.phoneNumber) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.recordingParticipants) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.targetQueue) {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.routingType) {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.viewId) {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.timeoutSeconds !== undefined) {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.loopCount !== undefined) {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.messages && displayInfo.messageCount > 0) {
        hasContent = true;
        contentLines += 1; // Header
        const messagesToShow = Math.min(3, displayInfo.messageCount);
        contentLines += messagesToShow;
        if (displayInfo.messageCount > 3) {
          contentLines += 1;
        }
      }
      
      if (displayInfo.textToSpeechVoice) {
        hasContent = true;
        contentLines += 1;
        if (displayInfo.textToSpeechEngine) contentLines += 1;
        if (displayInfo.textToSpeechStyle) contentLines += 1;
      }
      
      if (displayInfo.displayState) {
        hasContent = true;
        contentLines += 2;
      }
      
      if (displayInfo.flowModuleName) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.targetFlowName) {
        hasContent = true;
        contentLines += 1;
      }
      
      if (displayInfo.queueName && !displayInfo.metricType) {
        hasContent = true;
        contentLines += 1;
      }
      
      // Add blank line separator if there's content
      if (hasContent) {
        contentLines += 1;
      }
      
      // Calculate final height
      height = contentLines * LINE_HEIGHT + (this.NODE_PADDING * 2);
      height = Math.max(height, baseHeight);
    }
    
    // Snap to Lucid grid for clean alignment
    width = Math.ceil(width / this.LUCID_GRID_SIZE) * this.LUCID_GRID_SIZE;
    height = Math.ceil(height / this.LUCID_GRID_SIZE) * this.LUCID_GRID_SIZE;
    
    return { width, height };
  }

  /**
   * Generate rich text content for node
   */
  /**
   * Generate rich HTML content for node with comprehensive action type support
   * Returns HTML formatted string that matches React Flow detail level
   */
  generateRichTextContent(action, label, displayInfo) {
    // Helper to create styled spans
    const boldLabel = (text) => `<b style="font-size: 8pt;">${text}:</b>`;
    const normalText = (text) => `<span style="font-size: 8pt;">${text}</span>`;
    const metaText = (text) => `<span style="font-size: 7pt; color: #666;">${text}</span>`;
    const successText = (text) => `<span style="font-size: 7pt; color: #4CAF50;">${text}</span>`;
    
    // Escape HTML in user content
    const escapeHtml = (text) => {
      if (!text) return '';
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };
    
    // Start with empty content array
    const contentParts = [];
    
    // Add detailed content based on action type
    switch (action.Type) {
      case 'MessageParticipant':
        if (displayInfo.contentType === 'text' && displayInfo.displayMessage) {
          contentParts.push(normalText(`"${escapeHtml(displayInfo.displayMessage)}"`));
        } else if (displayInfo.contentType === 'ssml' && displayInfo.displaySSML) {
          contentParts.push(metaText('SSML:'));
          contentParts.push(normalText(`"${escapeHtml(displayInfo.displaySSML)}"`));
        } else if (displayInfo.contentType === 'prompt' && displayInfo.promptName) {
          contentParts.push(metaText('Prompt:'));
          contentParts.push(normalText(escapeHtml(displayInfo.promptName)));
        } else if (displayInfo.contentType === 'media' && displayInfo.mediaUri) {
          contentParts.push(metaText('Media:'));
          contentParts.push(normalText(escapeHtml(displayInfo.mediaUri)));
        }
        break;

      case 'GetParticipantInput':
        if (displayInfo.contentType === 'text' && displayInfo.displayPrompt) {
          contentParts.push(normalText(`"${escapeHtml(displayInfo.displayPrompt)}"`));
        } else if (displayInfo.contentType === 'ssml' && displayInfo.displaySSML) {
          contentParts.push(metaText('SSML:'));
          contentParts.push(normalText(`"${escapeHtml(displayInfo.displaySSML)}"`));
        } else if (displayInfo.contentType === 'prompt' && displayInfo.promptName) {
          contentParts.push(metaText('Prompt:'));
          contentParts.push(normalText(escapeHtml(displayInfo.promptName)));
        } else if (displayInfo.contentType === 'media' && displayInfo.mediaUri) {
          contentParts.push(metaText('Media:'));
          contentParts.push(normalText(escapeHtml(displayInfo.mediaUri)));
        }
        
        if (displayInfo.timeout) {
          contentParts.push(metaText(`Timeout: ${displayInfo.timeout}s`));
        }
        if (displayInfo.storeInput) {
          contentParts.push(successText('✓ Stores Input'));
        }
        break;

      case 'ConnectParticipantWithLexBot':
        // Display prompt content
        if (displayInfo.contentType === 'text' && displayInfo.displayPrompt) {
          contentParts.push(normalText(`"${escapeHtml(displayInfo.displayPrompt)}"`));
        } else if (displayInfo.contentType === 'ssml' && displayInfo.displaySSML) {
          contentParts.push(metaText('SSML:'));
          contentParts.push(normalText(`"${escapeHtml(displayInfo.displaySSML)}"`));
        } else if (displayInfo.contentType === 'prompt' && displayInfo.promptName) {
          contentParts.push(metaText('Prompt:'));
          contentParts.push(normalText(escapeHtml(displayInfo.promptName)));
        }
        
        if (displayInfo.timeout) {
          contentParts.push(metaText(`Timeout: ${displayInfo.timeout}s`));
        }
        break;

      case 'InvokeLambdaFunction':
        if (displayInfo.functionName) {
          contentParts.push(normalText(escapeHtml(displayInfo.functionName)));
        }
        if (displayInfo.timeout) {
          contentParts.push(metaText(`Timeout: ${displayInfo.timeout}s`));
        }
        if (displayInfo.lambdaInvocationAttributes && displayInfo.attributeCount > 0) {
          contentParts.push(metaText(`Invocation Attributes (${displayInfo.attributeCount}):`));
          const attributes = Object.entries(displayInfo.lambdaInvocationAttributes).slice(0, 3);
          attributes.forEach(([key, value]) => {
            contentParts.push(normalText(`${boldLabel(escapeHtml(key))} ${escapeHtml(String(value))}`));
          });
          if (displayInfo.attributeCount > 3) {
            contentParts.push(metaText(`+${displayInfo.attributeCount - 3} more...`));
          }
        }
        break;

      case 'UpdateContactAttributes':
        if (displayInfo.attributeCount > 0) {
          const attributes = Object.entries(displayInfo.attributes).slice(0, 3);
          attributes.forEach(([key, value]) => {
            contentParts.push(normalText(`${boldLabel(escapeHtml(key))} ${escapeHtml(String(value))}`));
          });
          if (displayInfo.attributeCount > 3) {
            contentParts.push(metaText(`+${displayInfo.attributeCount - 3} more...`));
          }
        }
        break;

      case 'CheckHoursOfOperation':
        if (displayInfo.hoursName && displayInfo.hoursName !== 'Check Hours') {
          contentParts.push(metaText('Hours:'));
          contentParts.push(normalText(escapeHtml(displayInfo.hoursName)));
        }
        break;

      case 'Compare':
        if (displayInfo.comparisonValue) {
          contentParts.push(normalText(escapeHtml(displayInfo.comparisonValue)));
        }
        break;

      case 'CheckMetricData':
        if (displayInfo.metricType) {
          contentParts.push(metaText(`Metric: ${boldLabel(escapeHtml(displayInfo.metricType))}`));
        }
        if (displayInfo.queueName) {
          contentParts.push(normalText(`Queue: ${escapeHtml(displayInfo.queueName)}`));
        }
        if (displayInfo.agentName) {
          contentParts.push(normalText(`Agent: ${escapeHtml(displayInfo.agentName)}`));
        }
        if (displayInfo.statistic && displayInfo.threshold && displayInfo.operator) {
          contentParts.push(normalText(`${escapeHtml(displayInfo.statistic)} ${escapeHtml(displayInfo.operator)} ${escapeHtml(String(displayInfo.threshold))}`));
        }
        break;

      case 'TransferParticipantToThirdParty':
        if (displayInfo.phoneNumber) {
          contentParts.push(normalText(escapeHtml(displayInfo.phoneNumber)));
          if (displayInfo.timeout) {
            contentParts.push(metaText(`Timeout: ${displayInfo.timeout}s`));
          }
        }
        break;

      case 'UpdateContactRecordingBehavior':
        if (displayInfo.recordingParticipants) {
          const participants = displayInfo.recordingParticipants;
          let recordingText = 'No recording';
          if (participants.length > 0) {
            if (participants.includes('Agent') && participants.includes('Customer')) {
              recordingText = 'Agent + Customer';
            } else if (participants.includes('Agent')) {
              recordingText = 'Agent only';
            } else if (participants.includes('Customer')) {
              recordingText = 'Customer only';
            }
          }
          contentParts.push(normalText(recordingText));
        }
        break;

      case 'CreateCallbackContact':
        if (displayInfo.targetQueue) {
          contentParts.push(normalText(`Queue: ${escapeHtml(displayInfo.targetQueue)}`));
        }
        contentParts.push(metaText(`Delay: ${displayInfo.initialDelay || 0}s, Max: ${displayInfo.maxAttempts || 1}`));
        break;

      case 'UpdateContactRoutingBehavior':
        if (displayInfo.routingType === 'priority') {
          contentParts.push(metaText('Queue Priority:'));
          contentParts.push(normalText(String(displayInfo.queuePriority)));
        } else if (displayInfo.routingType === 'timeAdjustment') {
          contentParts.push(metaText('Time Adjustment:'));
          contentParts.push(normalText(`${displayInfo.queueTimeAdjustment}s`));
        }
        break;

      case 'ShowView':
        if (displayInfo.viewId) {
          contentParts.push(metaText('View ID:'));
          contentParts.push(normalText(escapeHtml(displayInfo.viewId)));
        }
        if (displayInfo.timeout) {
          contentParts.push(metaText(`Timeout: ${displayInfo.timeout}s`));
        }
        break;

      case 'Wait':
        contentParts.push(metaText('Wait Duration:'));
        contentParts.push(normalText(`${displayInfo.timeoutSeconds}s`));
        break;

      case 'Loop':
        contentParts.push(metaText('Loop Count:'));
        contentParts.push(normalText(`${displayInfo.loopCount}x`));
        break;

      case 'MessageParticipantIteratively':
        if (displayInfo.messages && displayInfo.messages.length > 0) {
          contentParts.push(metaText(`Messages (${displayInfo.messageCount}):`));
          const messagesToShow = displayInfo.messages.slice(0, 3);
          messagesToShow.forEach((message) => {
            let messageContent = '';
            if (message.contentType === 'text') {
              messageContent = `${message.index}. "${escapeHtml(message.content)}"`;
            } else if (message.contentType === 'ssml') {
              messageContent = `${message.index}. SSML: "${escapeHtml(message.content)}"`;
            } else if (message.contentType === 'prompt') {
              messageContent = `${message.index}. Prompt: ${escapeHtml(message.content)}`;
            } else if (message.contentType === 'media') {
              messageContent = `${message.index}. Media: ${escapeHtml(message.content)}`;
            }
            contentParts.push(normalText(messageContent));
          });
          if (displayInfo.messages.length > 3) {
            contentParts.push(metaText(`+${displayInfo.messages.length - 3} more...`));
          }
        }
        break;

      case 'UpdateContactTextToSpeechVoice':
        if (displayInfo.textToSpeechVoice) {
          contentParts.push(normalText(`${boldLabel('Voice')} ${escapeHtml(displayInfo.textToSpeechVoice)}`));
        }
        if (displayInfo.textToSpeechEngine) {
          contentParts.push(normalText(`${boldLabel('Engine')} ${escapeHtml(displayInfo.textToSpeechEngine)}`));
        }
        if (displayInfo.textToSpeechStyle) {
          contentParts.push(normalText(`${boldLabel('Style')} ${escapeHtml(displayInfo.textToSpeechStyle)}`));
        }
        break;

      case 'UpdatePreviousContactParticipantState':
        if (displayInfo.displayState) {
          contentParts.push(metaText('Action:'));
          contentParts.push(normalText(escapeHtml(displayInfo.displayState)));
        }
        break;

      case 'InvokeFlowModule':
        if (displayInfo.flowModuleName) {
          contentParts.push(normalText(escapeHtml(displayInfo.flowModuleName)));
        }
        break;

      case 'CompleteOutboundCall':
        if (displayInfo.phoneNumber) {
          contentParts.push(normalText(`Number: ${escapeHtml(displayInfo.phoneNumber)}`));
        }
        break;

      case 'TransferToFlow':
        if (displayInfo.targetFlowName) {
          contentParts.push(normalText(escapeHtml(displayInfo.targetFlowName)));
        }
        break;

      case 'UpdateContactTargetQueue':
        if (displayInfo.queueName) {
          contentParts.push(normalText(escapeHtml(displayInfo.queueName)));
        }
        break;

      default:
        // No additional content for other action types
        break;
    }
    
    // Join all content parts with line breaks
    return contentParts.length > 0 ? contentParts.join('<br>') : '';
  }

  /**
   * Get entry node style
   */
  getEntryNodeStyle() {
    return {
      fill: {
        type: 'color',
        color: '#4CAF50'
      },
      stroke: {
        color: '#2E7D32',
        width: 2
      }
    };
  }

  /**
   * Get professional node style with shadows and gradients
   */
  getProfessionalNodeStyle(category, nodeType, actionType) {
    // Enhanced category-based color scheme 
    const categoryColors = {
      'communication': { 
        fill: '#E3F2FD', 
        stroke: '#1976D2'
      },
      'input': { 
        fill: '#FFF3E0', 
        stroke: '#F57C00'
      },
      'integration': { 
        fill: '#F3E5F5', 
        stroke: '#7B1FA2'
      },
      'routing': { 
        fill: '#E8F5E8', 
        stroke: '#388E3C'
      },
      'validation': { 
        fill: '#FFF8E1', 
        stroke: '#FBC02D'
      },
      'terminator': { 
        fill: '#FFEBEE', 
        stroke: '#D32F2F'
      },
      'recording': { 
        fill: '#E0F2F1', 
        stroke: '#00796B'
      },
      'data': { 
        fill: '#F1F8E9', 
        stroke: '#689F38'
      },
      'transfer': { 
        fill: '#FCE4EC', 
        stroke: '#C2185B'
      },
      'callback': { 
        fill: '#E8EAF6', 
        stroke: '#3F51B5'
      },
      'system': { 
        fill: '#F5F5F5', 
        stroke: '#616161'
      },
      'general': { 
        fill: '#FAFAFA', 
        stroke: '#757575'
      }
    };
    
    const colors = categoryColors[category] || categoryColors.general;
    
    // Base professional styling with proper Lucid API format
    const baseStyle = {
      fill: {
        type: 'color',
        color: colors.fill
      },
      stroke: {
        color: colors.stroke,
        width: 2.5
      },
      opacity: 95 // Lucid expects whole number 0-100
    };
    
    // Special styling for certain node types
    if (nodeType === 'entryNode') {
      baseStyle.stroke.width = 3;
      baseStyle.fill = {
        type: 'color',
        color: '#4CAF50'
      };
      baseStyle.stroke.color = '#2E7D32';
    } else if (nodeType === 'endNode') {
      baseStyle.stroke.width = 3;
      baseStyle.fill = {
        type: 'color',
        color: '#F44336'
      };
      baseStyle.stroke.color = '#C62828';
    }
    
    // Enhance certain action types with supported properties
    switch (actionType) {
      case 'InvokeLambdaFunction':
        baseStyle.stroke.style = 'dashed'; // Dashed border for integrations
        break;
      case 'Wait':
        baseStyle.stroke.style = 'dashed'; // Dashed borders for delays
        break;
      default:
        // No special styling for other action types
        break;
    }
    
    return baseStyle;
  }

  /**
   * Get professional text style with proper typography
   */
  getProfessionalTextStyle(category, shapeType) {
    // Base typography settings - using 8pt for better readability in Lucid Charts
    let fontSize = 8;
    let fontWeight = 500;

    // Adjust font size and weight for different shapes
    switch (shapeType) {
      case 'circle':
        fontSize = 8;
        fontWeight = 700;
        break;
      case 'decision':
        fontSize = 8;
        fontWeight = 600;
        break;
      case 'document':
      case 'manualInput':
        fontSize = 8;
        break;
      case 'predefinedProcess':
        fontSize = 8;
        fontWeight = 600;
        break;
      default:
        fontSize = 8;
    }
    
    // Category-specific text colors for better contrast
    const categoryTextColors = {
      'terminator': '#B71C1C',
      'validation': '#E65100',
      'integration': '#4A148C',
      'routing': '#1B5E20'
    };
    
    const textColor = categoryTextColors[category] || '#2C3E50';
    
    // Use a metric-standard font that matches Lucid's default renderer (and is available to
    // the browser for measurement), so measureLucidHtml predicts Lucid's wrapping/height and
    // text doesn't overflow the shape. (Inter could be substituted by a wider font in Lucid.)
    // line-height 1.4 matches the measurement environment.
    return `font-family: Arial, Helvetica, 'Liberation Sans', sans-serif; font-size: ${fontSize}pt; font-weight: ${fontWeight}; text-align: center; color: ${textColor}; line-height: 1.4;`;
  }

  /**
   * Convert React Flow coordinates to Lucid coordinates with intelligent scaling
   */
  /**
   * Generate Lucid-compliant ID that meets API requirements
   * - 1-36 characters maximum
   * - Only alphanumeric characters and -_.~
   */
  generateLucidId(prefix = '', source = '') {
    // Create short hash from source for uniqueness while keeping it readable
    let hash;
    if (source) {
      // Create a hash from the source string for uniqueness
      hash = source.split('').reduce((acc, char) => {
        return (acc << 5) - acc + char.charCodeAt(0) & 0xfffff;
      }, 0);
      // Convert to base36 to get alphanumeric representation
      hash = Math.abs(hash).toString(36);
    } else {
      // Generate random hash for cases without source
      hash = Math.random().toString(36).substr(2, 8);
    }
    
    // Combine prefix and hash, ensure within 36 character limit
    let id = `${prefix}${hash}`.substr(0, 36);
    
    // Remove any invalid characters (keep only alphanumeric and -_.~)
    id = id.replace(/[^a-zA-Z0-9\-_.~]/g, '');
    
    // Ensure minimum length of 1 character
    if (id.length === 0) {
      id = 'id' + Math.random().toString(36).substr(2, 4);
    }
    
    return id;
  }

  // Test method to validate Lucid ID compliance
  validateLucidId(id) {
    const requirements = {
      length: id.length >= 1 && id.length <= 36,
      characters: /^[a-zA-Z0-9\-_.~]+$/.test(id),
      notEmpty: id.trim().length > 0
    };

    const isValid = requirements.length && requirements.characters && requirements.notEmpty;

    if (!isValid) {
      console.error(`Invalid Lucid ID: "${id}" (${id.length} chars)`, requirements);
    }

    return isValid;
  }

  /**
   * Get correct shape ID for node references (consistent between nodes and edges)
   */
  getShapeId(nodeId) {
    // Special handling for Entry node - use its hardcoded ID directly
    if (nodeId === 'entry-node') {
      return 'entry-node';
    }
    // For all other nodes, use the generated ID format
    return this.generateLucidId('nd-', nodeId);
  }

  getLucidCoordinates(reactFlowPosition) {
    // Validate input coordinates to prevent NaN
    if (!reactFlowPosition || typeof reactFlowPosition !== 'object') {
      this.log('debug', 'Invalid position object provided, using fallback:', reactFlowPosition);
      return { x: 120, y: 120 }; // Safe fallback coordinates
    }
    
    if (typeof reactFlowPosition.x !== 'number' || typeof reactFlowPosition.y !== 'number') {
      this.log('debug', 'Position x or y is not a number, using fallback:', reactFlowPosition);
      return { x: 120, y: 120 }; // Safe fallback coordinates
    }
    
    // Check for NaN or infinite values
    if (isNaN(reactFlowPosition.x) || isNaN(reactFlowPosition.y) || 
        !isFinite(reactFlowPosition.x) || !isFinite(reactFlowPosition.y)) {
      this.log('debug', 'NaN or infinite coordinates detected, using fallback:', reactFlowPosition);
      return { x: 120, y: 120 }; // Safe fallback coordinates
    }
    
    // Enhanced coordinate conversion with layout direction support
    const scale = this.layoutDirection === 'LR' ? 1.8 : 1.5;
    const offsetX = 120; // Larger margin for professional appearance
    const offsetY = 120;
    
    // Apply layout direction adjustments
    let x, y;
    if (this.layoutDirection === 'LR') {
      // Left-to-right: optimize horizontal spacing
      x = (reactFlowPosition.x * scale + offsetX) / this.LUCID_GRID_SIZE;
      y = (reactFlowPosition.y * (scale * 0.8) + offsetY) / this.LUCID_GRID_SIZE;
    } else {
      // Top-to-bottom: optimize vertical spacing
      x = (reactFlowPosition.x * (scale * 0.9) + offsetX) / this.LUCID_GRID_SIZE;
      y = (reactFlowPosition.y * scale + offsetY) / this.LUCID_GRID_SIZE;
    }
    
    // Round to grid and ensure valid numbers
    x = Math.round(x) * this.LUCID_GRID_SIZE;
    y = Math.round(y) * this.LUCID_GRID_SIZE;
    
    // Final validation to ensure valid numbers
    if (isNaN(x) || isNaN(y) || !isFinite(x) || !isFinite(y)) {
      this.log('debug', 'Coordinate calculation resulted in NaN, using fallback');
      return { x: 120, y: 120 };
    }
    
    // Ensure coordinates are within reasonable bounds (not negative or extremely large)
    x = Math.max(20, Math.min(x, 5000)); // Reasonable bounds for Lucid Charts
    y = Math.max(20, Math.min(y, 5000));
    
    return { x, y };
  }

  /**
   * Apply dagre-based layout optimization for professional node positioning
   * Uses the same algorithm as React Flow viewer for consistent quality
   */
  optimizeLayoutPositions(nodes, transitions) {
    try {
      this.log('info', 'Applying dagre layout optimization for professional positioning');

      // Convert to dagre format and apply layout
      const dagreNodes = this.convertNodesToDagreFormat(nodes, transitions);
      const dagreEdges = this.convertTransitionsToDagreFormat(transitions);

      // Debug logging to verify ID matching
      this.log('debug', `Dagre nodes (${dagreNodes.length}):`, dagreNodes.map(n => n.id));
      this.log('debug', `Dagre edges (${dagreEdges.length}):`, dagreEdges.map(e => `${e.source} → ${e.target}`));

      // Create fresh dagre graph
      const dagreGraph = new dagre.graphlib.Graph();
      dagreGraph.setDefaultEdgeLabel(() => ({}));

      // Configure dagre layout parameters (same as React Flow viewer)
      const isHorizontal = this.layoutDirection === 'LR';

      // Calculate intelligent spacing based on node dimensions
      const { maxNodeWidth, maxNodeHeight, avgNodeWidth, avgNodeHeight } = this.calculateNodeDimensionStats(dagreNodes);
      const { dynamicNodeSep, dynamicRankSep } = this.calculateIntelligentSpacing(
        isHorizontal, maxNodeWidth, maxNodeHeight, avgNodeWidth, avgNodeHeight
      );

      dagreGraph.setGraph({
        rankdir: this.layoutDirection,
        nodesep: dynamicNodeSep,
        ranksep: dynamicRankSep,
        marginx: Math.max(50, maxNodeWidth * 0.2),
        marginy: Math.max(50, maxNodeHeight * 0.2)
      });

      // Add nodes to dagre graph with their calculated dimensions
      dagreNodes.forEach(node => {
        dagreGraph.setNode(node.id, { width: node.width, height: node.height });
      });

      // Add edges to dagre graph
      dagreEdges.forEach(edge => {
        dagreGraph.setEdge(edge.source, edge.target);
      });

      // Apply dagre layout algorithm
      dagre.layout(dagreGraph);

      // Apply dagre positions back to Lucid nodes
      let positionedNodes = 0;
      nodes.forEach(node => {
        const nodeWithPosition = dagreGraph.node(node.id);
        if (nodeWithPosition) {
          // Convert dagre coordinates to Lucid coordinates
          const lucidPosition = this.convertDagrePositionToLucid(nodeWithPosition);
          node.boundingBox.x = lucidPosition.x;
          node.boundingBox.y = lucidPosition.y;
          positionedNodes++;
        } else {
          this.log('warn', `Node ${node.id} not found in dagre graph after layout`);
        }
      });

      // Apply final grid snapping and bounds checking
      this.applyFinalPositionOptimizations(nodes);

      this.log('info', `Dagre layout optimization completed successfully: ${positionedNodes}/${nodes.length} nodes positioned`);
    } catch (error) {
      this.log('debug', 'Dagre layout optimization failed, using fallback positions:', error);
      this.applyFallbackLayout(nodes);
    }

    return nodes;
  }

  /**
   * Convert Lucid nodes to dagre format with proper dimensions
   */
  convertNodesToDagreFormat(nodes, transitions) {
    return nodes.map(node => {
      // Use existing node dimensions or calculate if missing
      const width = node.boundingBox.w || this.NODE_MIN_WIDTH;
      const height = node.boundingBox.h || this.NODE_MIN_HEIGHT;

      return {
        id: node.id,
        width: Math.max(50, Math.min(1200, width)),
        height: Math.max(30, Math.min(600, height))
      };
    });
  }

  /**
   * Convert transitions to dagre edge format
   */
  convertTransitionsToDagreFormat(transitions) {
    return transitions.map(transition => ({
      source: this.getShapeId(transition.from),
      target: this.getShapeId(transition.to)
    }));
  }

  /**
   * Calculate node dimension statistics for intelligent spacing
   */
  calculateNodeDimensionStats(dagreNodes) {
    let maxNodeWidth = 0;
    let maxNodeHeight = 0;
    let totalWidth = 0;
    let totalHeight = 0;

    dagreNodes.forEach(({ width, height }) => {
      maxNodeWidth = Math.max(maxNodeWidth, width);
      maxNodeHeight = Math.max(maxNodeHeight, height);
      totalWidth += width;
      totalHeight += height;
    });

    const avgNodeWidth = totalWidth / dagreNodes.length;
    const avgNodeHeight = totalHeight / dagreNodes.length;

    return { maxNodeWidth, maxNodeHeight, avgNodeWidth, avgNodeHeight };
  }

  /**
   * Calculate intelligent spacing using same algorithm as React Flow
   */
  calculateIntelligentSpacing(isHorizontal, maxNodeWidth, maxNodeHeight, avgNodeWidth, avgNodeHeight) {
    const baseNodeSep = isHorizontal ? 100 : 80;
    const baseRankSep = isHorizontal ? 150 : 120;

    // Minimum safe spacing (prevents worst-case overlaps for largest nodes)
    const minSafeNodeSep = maxNodeWidth + Math.max(baseNodeSep * 0.4, 20);
    const minSafeRankSep = maxNodeHeight + Math.max(baseRankSep * 0.4, 15);

    // Optimal spacing based on average node sizes (better for typical nodes)
    const optimalNodeSep = avgNodeWidth * 1.3 + Math.max(baseNodeSep * 0.6, 25);
    const optimalRankSep = avgNodeHeight * 1.3 + Math.max(baseRankSep * 0.6, 20);

    // Use adaptive spacing: safety first, but optimize when possible
    const dynamicNodeSep = Math.max(minSafeNodeSep, optimalNodeSep);
    const dynamicRankSep = Math.max(minSafeRankSep, optimalRankSep);

    return { dynamicNodeSep, dynamicRankSep };
  }

  /**
   * Convert dagre position to Lucid coordinate system
   */
  convertDagrePositionToLucid(nodeWithPosition) {
    // Dagre returns center position, convert to top-left for Lucid
    const x = nodeWithPosition.x - (nodeWithPosition.width / 2);
    const y = nodeWithPosition.y - (nodeWithPosition.height / 2);

    return { x, y };
  }

  /**
   * Apply final position optimizations (grid snapping, bounds checking)
   */
  applyFinalPositionOptimizations(nodes) {
    nodes.forEach(node => {
      // Ensure nodes don't go too close to edges
      node.boundingBox.x = Math.max(60, node.boundingBox.x);
      node.boundingBox.y = Math.max(60, node.boundingBox.y);

      // Snap to grid for clean alignment
      node.boundingBox.x = Math.round(node.boundingBox.x / this.LUCID_GRID_SIZE) * this.LUCID_GRID_SIZE;
      node.boundingBox.y = Math.round(node.boundingBox.y / this.LUCID_GRID_SIZE) * this.LUCID_GRID_SIZE;
    });
  }

  /**
   * Fallback layout if dagre fails
   */
  applyFallbackLayout(nodes) {
    nodes.forEach((node, index) => {
      const cols = Math.ceil(Math.sqrt(nodes.length));
      const x = (index % cols) * 200 + 120;
      const y = Math.floor(index / cols) * 150 + 120;

      node.boundingBox.x = x;
      node.boundingBox.y = y;
    });
  }

  /**
   * Convert Lucid nodes to EdgeRoutingSystem format
   */
  convertLucidNodesToRoutingFormat(lucidNodes) {
    return lucidNodes.map(node => ({
      id: node.id,
      position: {
        x: node.boundingBox.x,
        y: node.boundingBox.y
      },
      dimensions: {
        width: node.boundingBox.w,
        height: node.boundingBox.h
      },
      type: node.customData?.nodeType || 'processNode'
    }));
  }

  /**
   * Convert transitions to EdgeRoutingSystem format
   */
  convertTransitionsToRoutingFormat(transitions) {
    return transitions.map((transition, index) => ({
      id: `edge-${transition.from}-${transition.to}-${index}`,
      source: transition.from,
      target: transition.to,
      type: transition.type,
      data: {
        transitionType: transition.type,
        label: transition.label
      }
    }));
  }

  /**
   * Get action position from metadata
   */
  getActionPosition(actionId) {
    const metadata = this.metadata[actionId];
    if (metadata && metadata.position) {
      return {
        x: metadata.position.x || 0,
        y: metadata.position.y || 0
      };
    }
    return null;
  }

  /**
   * Calculate node position if not in metadata
   */
  calculateNodePosition(action) {
    // Simple grid-based positioning as fallback
    const allActionIds = Array.from(this.actions.keys());
    const actionIndex = allActionIds.indexOf(action.Identifier);
    
    if (actionIndex === -1) {
      this.log('debug', 'Action not found in actions map, using random position');
      // Use random position as last resort
      const randomX = Math.random() * 400;
      const randomY = Math.random() * 300 + 150; // Avoid entry node area
      return { x: randomX, y: randomY };
    }
    
    const columns = 4;
    const row = Math.floor(actionIndex / columns);
    const col = actionIndex % columns;
    
    const x = col * 200;
    const y = row * 150 + 100; // Offset for entry node
    
    // Ensure valid coordinates
    const validX = isNaN(x) ? 100 + (actionIndex * 50) : x;
    const validY = isNaN(y) ? 150 + (actionIndex * 50) : y;
    
    return {
      x: validX,
      y: validY
    };
  }

  /**
   * Analyze edge connections to determine connection points for each edge
   * This prevents overlapping lines by spreading connection points around node perimeters
   */
  analyzeEdgeConnections(transitions) {
    const connectionAnalysis = {
      outgoingEdges: new Map(), // nodeId -> array of transition info
      incomingEdges: new Map()  // nodeId -> array of transition info
    };

    transitions.forEach((transition, index) => {
      // Track outgoing edges from source
      const sourceId = transition.from;
      if (!connectionAnalysis.outgoingEdges.has(sourceId)) {
        connectionAnalysis.outgoingEdges.set(sourceId, []);
      }
      connectionAnalysis.outgoingEdges.get(sourceId).push({
        transition,
        transitionIndex: index,
        type: transition.type,
        label: transition.label
      });

      // Track incoming edges to target
      const targetId = transition.to;
      if (!connectionAnalysis.incomingEdges.has(targetId)) {
        connectionAnalysis.incomingEdges.set(targetId, []);
      }
      connectionAnalysis.incomingEdges.get(targetId).push({
        transition,
        transitionIndex: index,
        type: transition.type,
        label: transition.label
      });
    });

    return connectionAnalysis;
  }

  /**
   * Calculate connection point position on a shape's perimeter
   * Returns {x, y} where x and y are 0-1 (relative position on shape)
   */
  calculateConnectionPoint(nodeId, edgeIndex, totalEdges, isSource, nodes) {
    // Find the node to check its type
    const node = nodes.find(n => n.id === this.getShapeId(nodeId));
    const nodeType = node?.type || 'process';

    // TARGET ENDPOINTS: Always use centered entry points for better readability
    // This matches Contact Flow Viewer where all lines enter nodes at the same point
    if (!isSource) {
      return nodeType === 'decision'
        ? { x: 0.5, y: 0.0 }   // Top-center for decisions
        : { x: 0.5, y: 0.0 };   // Top-center for all nodes
    }

    // SOURCE ENDPOINTS: Spread exit points to prevent overlapping lines
    // Single edge - use centered exit point
    if (totalEdges === 1) {
      return nodeType === 'decision'
        ? { x: 0.5, y: 1.0 }   // Bottom-center for decisions
        : { x: 0.5, y: 1.0 };   // Bottom-center for others
    }

    // Multiple edges - spread them out along the bottom edge
    if (nodeType === 'decision') {
      // Decision nodes: spread around bottom corners (left and right)
      if (totalEdges === 2) {
        return edgeIndex === 0
          ? { x: 0.3, y: 0.85 }  // Bottom-left area
          : { x: 0.7, y: 0.85 }; // Bottom-right area
      } else {
        // 3+ edges: distribute around bottom
        const step = 0.6 / (totalEdges + 1);
        const x = 0.2 + step * (edgeIndex + 1);
        return { x: x, y: 0.9 };
      }
    } else {
      // Process/other nodes: spread along bottom edge
      const step = 0.7 / (totalEdges + 1);  // Leave margins (0.15 on each side)
      const x = 0.15 + step * (edgeIndex + 1);
      return { x: x, y: 1.0 };  // Bottom edge
    }
  }

  /**
   * Create intelligent Lucid lines from transitions
   */
  createLucidLines(transitions, nodes) {
    // Analyze edge connections to determine connection points
    const connectionAnalysis = this.analyzeEdgeConnections(transitions);

    // Group transitions by source-target pairs to handle parallel edges
    const edgeGroups = new Map();
    
    transitions.forEach((transition, index) => {
      const key = `${transition.from}-${transition.to}`;
      if (!edgeGroups.has(key)) {
        edgeGroups.set(key, []);
      }
      edgeGroups.get(key).push({ ...transition, originalIndex: index });
    });

    const lucidLines = [];
    
    // Process each group and create professional lines with connection analysis
    edgeGroups.forEach((groupTransitions, edgeKey) => {
      const totalLines = groupTransitions.length;

      groupTransitions.forEach((transition, groupIndex) => {
        const lucidLine = this.createProfessionalLucidLine(
          transition,
          transition.originalIndex,
          groupIndex,
          totalLines,
          nodes,
          connectionAnalysis  // Pass connection analysis for spreading connection points
        );
        lucidLines.push(lucidLine);
      });
    });

    return lucidLines;
  }

  /**
   * Create professional Lucid line with intelligent routing and labeling
   * Now includes connection point spreading to prevent overlapping lines
   */
  createProfessionalLucidLine(transition, globalIndex, groupIndex, totalInGroup, nodes, connectionAnalysis) {
    //const lineColor = this.getLineColor(transition.type);
    const lineType = this.getLineType(transition.type);
    const lineStyle = this.getProfessionalLineStyle(transition.type);

    // Create unique ID using generateLucidId for compliance
    const uniqueId = this.generateLucidId('ln-', `${transition.from}-${transition.to}-${globalIndex}`);

    // Calculate routing offset for parallel lines
    let routingOffset = 0;
    if (totalInGroup > 1) {
      const spacing = 25; // pixels between parallel lines
      const center = (totalInGroup - 1) / 2;
      routingOffset = (groupIndex - center) * spacing;
    }

    // Calculate connection points for source and target to spread edges around node perimeters
    const sourceNodeId = transition.from;
    const targetNodeId = transition.to;

    // Get all edges for source and target nodes
    const sourceEdges = connectionAnalysis.outgoingEdges.get(sourceNodeId) || [];
    const targetEdges = connectionAnalysis.incomingEdges.get(targetNodeId) || [];

    // Find this transition's index in the source's outgoing edges
    const sourceEdgeIndex = sourceEdges.findIndex(e => e.transitionIndex === globalIndex);
    const sourcePosition = this.calculateConnectionPoint(
      sourceNodeId,
      sourceEdgeIndex >= 0 ? sourceEdgeIndex : 0,
      sourceEdges.length,
      true,  // isSource
      nodes
    );

    // Find this transition's index in the target's incoming edges
    const targetEdgeIndex = targetEdges.findIndex(e => e.transitionIndex === globalIndex);
    const targetPosition = this.calculateConnectionPoint(
      targetNodeId,
      targetEdgeIndex >= 0 ? targetEdgeIndex : 0,
      targetEdges.length,
      false,  // isTarget
      nodes
    );

    // Create professional line with Lucid Standard Import format
    const lucidLine = {
      id: uniqueId,
      lineType: lineType, // Required field for Lucid API
      endpoint1: {
        type: 'shapeEndpoint',
        style: 'none', // Required style field
        shapeId: this.getShapeId(transition.from),
        position: sourcePosition  // Spread connection points around perimeter
      },
      endpoint2: {
        type: 'shapeEndpoint',
        style: this.getEndpointStyle(transition.type), // Arrow style based on transition type
        shapeId: this.getShapeId(transition.to),
        position: targetPosition  // Spread connection points around perimeter
      },
      stroke: lineStyle, // Move style to stroke property
      // Fix: Ensure all customData values are strings as required by Lucid API
      customData: [
        {
          key: 'transitionType',
          value: String(transition.type)
        }, 
        {
          key: 'routingOffset', 
          value: String(routingOffset)
        }, 
        {
          key: 'groupIndex',
          value: String(groupIndex)
        }, 
        {
          key: 'totalInGroup',
          value: String(totalInGroup)
        }
      ]
    };

    // NOTE: Inline line labels removed in favor of rotated label shapes
    // Rotated label shapes provide better readability and match Contact Flow Viewer appearance
    // Labels are now created as separate shapes positioned at exit points with -45° rotation

    // Compute elbow control points (absolute page coords). These route the line
    // orthogonally around intermediate nodes (via A* when obstacles exist) and are
    // honored by Lucid because the line is `lineType: 'elbow'`. An empty array lets
    // Lucid draw a clean direct elbow between the two perimeter endpoints.
    const elbowControlPoints = this.calculateElbowControlPoints(
      transition,
      nodes,
      sourcePosition,
      targetPosition,
      routingOffset
    );
    if (elbowControlPoints.length > 0) {
      lucidLine.elbowControlPoints = elbowControlPoints;
    }

    return lucidLine;
  }

  /**
   * Get professional line style based on transition type
   */
  getProfessionalLineStyle(transitionType) {
    // Return stroke properties that match Lucid API format
    // Properties: color (hex string), width (integer), style (solid/dashed/dotted)
    switch (transitionType) {
      case 'success':
        return {
          color: '#4CAF50',
          width: 2
        };
      case 'error':
        return {
          color: '#F44336',
          width: 2,
          style: 'dashed'
        };
      case 'condition':
        return {
          color: '#2196F3',
          width: 2
        };
      default:
        return {
          color: '#666666',
          width: 2
        };
    }
  }

  /**
   * Get line end styles for arrows
   */
  getLineEndStyles(transitionType) {
    const baseEndStyle = {
      end: 'arrow',
      start: 'none'
    };

    switch (transitionType) {
      case 'error':
        return {
          ...baseEndStyle,
          end: 'openArrow' // Open arrow for errors
        };
      case 'condition':
        return {
          ...baseEndStyle,
          end: 'arrow' // Solid arrow for conditions
        };
      default:
        return baseEndStyle;
    }
  }

  /**
   * Get endpoint style for transitions (used in new line format)
   */
  getEndpointStyle(transitionType) {
    // Use openArrow for all line types for consistent arrow display
    // openArrow provides a clear, visible arrow that works well for all transition types
    return 'openArrow';
  }

  /**
   * Create intelligent line label with professional styling
   */
  createIntelligentLineLabel(transition, lineColor) {
    // Use clean label text without emoji icons for professional appearance
    let labelText = transition.label;

    // Keep labels clean and simple without type-specific enhancements
    // This provides a more professional look that matches the Contact Flow Viewer

    return {
      text: labelText,
      position: 0.25, // Position closer to source node for better readability
      side: 'top', // Changed from 'placement' to 'side' per Lucid API requirements
      style: {
        fontSize: "10",
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        fontWeight: "500",
        color: {
          type: 'color',
          color: lineColor
        },
        backgroundColor: {
          type: 'color',
          color: '#FFFFFF'
        },
        borderColor: {
          type: 'color',
          color: lineColor
        },
        borderWidth: 1,
        borderRadius: 4,
        padding: '3,6,3,6',
        shadowColor: 'rgba(0,0,0,0.1)',
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        shadowBlur: 2
      }
    };
  }

  /**
   * Create rotated label shapes positioned near node exit points
   * This provides better readability than inline line labels which don't support rotation
   * Mimics the Contact Flow Viewer's -45° rotated labels attached to exit points
   */
  createLabelShapes(transitions, shapes, connectionAnalysis) {
    const labelShapes = [];
    
    // Process each transition that has a label
    transitions.forEach((transition, globalIndex) => {
      // Skip transitions without labels
      if (!transition.label || transition.label.trim() === '') {
        return;
      }
      
      // Find the source node shape
      const sourceShapeId = this.getShapeId(transition.from);
      const sourceShape = shapes.find(s => s.id === sourceShapeId);
      
      if (!sourceShape || !sourceShape.boundingBox) {
        this.log('warn', `Cannot create label shape: source shape ${sourceShapeId} not found`);
        return;
      }
      
      // Get connection point for this transition
      const sourceEdges = connectionAnalysis.outgoingEdges.get(transition.from) || [];
      const sourceEdgeIndex = sourceEdges.findIndex(e => e.transitionIndex === globalIndex);
      const connectionPoint = this.calculateConnectionPoint(
        transition.from,
        sourceEdgeIndex >= 0 ? sourceEdgeIndex : 0,
        sourceEdges.length,
        true,  // isSource
        shapes
      );
      
      // Calculate absolute position of the exit point
      const bbox = sourceShape.boundingBox;
      const exitPointX = bbox.x + (connectionPoint.x * bbox.w);
      const exitPointY = bbox.y + (connectionPoint.y * bbox.h);

      // Estimate label dimensions to prevent text overflow
      // Character width of 7px provides accurate sizing for 5pt bold font
      // Padding of 12px provides very tight fit while preventing text wrapping
      const labelText = transition.label;
      const charWidth = 7;  // Accurate for 5pt font-weight 600
      const estimatedWidth = Math.max(40, labelText.length * charWidth + 12);  // Very tight horizontal padding
      const estimatedHeight = 26;  // Height maintained for readability with smaller font

      // Position the label along the FIRST segment of the elbow route — i.e. the
      // direction the line leaves the node (down for a bottom exit, right for a right
      // exit, etc.). This keeps the label sitting on the line where it originates, like
      // the React Flow viewer, and stays correct now that lines are elbow-routed (the
      // old "toward target center" vector did not match the orthogonal route).
      const exitDir = this.getConnectionAxisDirection(connectionPoint);

      // Clear the label from the node. Because the label is rotated -45°, its bounding box
      // expands diagonally — its half-extent from center is ~(w+h)*0.36 — so a flat 24px
      // offset let a wide label reach back into the node. Derive the base distance from the
      // rotated footprint so the label always sits past the node edge.
      const rotatedHalfExtent = (estimatedWidth + estimatedHeight) * 0.36;
      const baseLabelDistance = Math.max(24, rotatedHalfExtent + 8);

      // Cascade the distance from the node by sibling index so labels on dense
      // decision/menu nodes (many edges leaving one node) never stack on each other, but cap
      // the distance to the line's straight stub so the label always sits ON the line (the
      // line goes straight for LINE_STUB px before it may turn). Sibling edges leave from
      // spread exit points (different x), which provides horizontal separation within the stub.
      const siblingStep = 12;
      const labelDistance = Math.min(
        baseLabelDistance + (sourceEdges.length > 1 ? Math.max(0, sourceEdgeIndex) * siblingStep : 0),
        this.LINE_STUB - 6
      );

      const labelX = exitPointX + (exitDir.x * labelDistance) - (estimatedWidth / 2);
      const labelY = exitPointY + (exitDir.y * labelDistance) - (estimatedHeight / 2);

      // Get line color for consistent styling
      const lineColor = this.getLineColor(transition.type);
      
      // Create text shape with rotation
      // Note: rotation property must be in boundingBox per Lucid API specification
      // Lucid API requires rotation between 0-360 (clockwise), so -45° = 315°
      const labelShape = {
        id: this.generateLucidId('lbl-', `${transition.from}-${transition.to}-${globalIndex}`),
        type: 'rectangle',  // Rectangle shape supports rotation
        boundingBox: {
          x: labelX,
          y: labelY,
          w: estimatedWidth,
          h: estimatedHeight,
          rotation: 315  // -45° counter-clockwise = 315° clockwise (0-360 range required by Lucid API)
        },
        // Text must be HTML with inline styles per Lucid API specification
        // Replace spaces with &nbsp; to prevent wrapping (white-space CSS not supported by Lucid)
        text: `<p style="font-size: 5pt; color: ${lineColor}; font-weight: 600; text-align: center; vertical-align: middle; font-family: Arial, Helvetica, 'Liberation Sans', sans-serif">${labelText.replace(/ /g, '&nbsp;')}</p>`,
        style: {
          fill: {
            type: 'color',
            color: '#FFFFFF'  // White background
          },
          stroke: {
            color: lineColor,
            width: 1.5  // Slightly thicker border for better visibility
          },
          cornerRadius: 3
        },
        // Add custom data to track this as a label shape
        customData: [
          {
            key: 'isLabelShape',
            value: 'true'
          },
          {
            key: 'sourceTransition',
            value: String(globalIndex)
          }
        ],
        zIndex: 1
      };
      
      labelShapes.push(labelShape);
      this.log('debug', `Created rotated label shape for transition ${transition.from} -> ${transition.to}: "${labelText}" at (${Math.round(labelX)}, ${Math.round(labelY)})`);
    });
    
    this.log('info', `Created ${labelShapes.length} rotated label shapes`);
    return labelShapes;
  }

  /**
   * Get optimal connection side for cleaner routing
   */
  getOptimalConnectionSide(fromId, toId, nodes, endpoint) {
    // Find node positions
    const fromNode = nodes.find(n => n.id === fromId);
    const toNode = nodes.find(n => n.id === toId);
    
    if (!fromNode || !toNode) {
      return endpoint === 'source' ? 'right' : 'left';
    }
    
    const fromX = fromNode.boundingBox.x + fromNode.boundingBox.w / 2;
    const fromY = fromNode.boundingBox.y + fromNode.boundingBox.h / 2;
    const toX = toNode.boundingBox.x + toNode.boundingBox.w / 2;
    const toY = toNode.boundingBox.y + toNode.boundingBox.h / 2;
    
    if (endpoint === 'source') {
      // Determine best exit side from source node
      const deltaX = toX - fromX;
      const deltaY = toY - fromY;
      
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        return deltaX > 0 ? 'right' : 'left';
      } else {
        return deltaY > 0 ? 'bottom' : 'top';
      }
    } else {
      // Determine best entry side for target node
      const deltaX = fromX - toX;
      const deltaY = fromY - toY;
      
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        return deltaX > 0 ? 'right' : 'left';
      } else {
        return deltaY > 0 ? 'bottom' : 'top';
      }
    }
  }

  /**
   * Check if transition needs custom routing
   */
  needsCustomRouting(transition, nodes) {
    // Find source and target nodes
    const sourceNode = nodes.find(n => n.id === transition.from);
    const targetNode = nodes.find(n => n.id === transition.to);
    
    if (!sourceNode || !targetNode) return false;
    
    // Check if nodes overlap or are very close
    const sourceBox = sourceNode.boundingBox;
    const targetBox = targetNode.boundingBox;
    
    const horizontalOverlap = !(sourceBox.x + sourceBox.w < targetBox.x || targetBox.x + targetBox.w < sourceBox.x);
    const verticalOverlap = !(sourceBox.y + sourceBox.h < targetBox.y || targetBox.y + targetBox.h < sourceBox.y);
    
    // Need custom routing if nodes are overlapping or very close
    const distance = Math.sqrt(
      Math.pow(targetBox.x - sourceBox.x, 2) + Math.pow(targetBox.y - sourceBox.y, 2)
    );
    
    return horizontalOverlap || verticalOverlap || distance < 150;
  }

  /**
   * Compute the absolute (x, y) of a perimeter connection point for a node.
   * position is the 0-1 relative coordinate returned by calculateConnectionPoint().
   */
  getAbsoluteConnectionPoint(node, position) {
    const b = node.boundingBox;
    return {
      x: b.x + (position.x * b.w),
      y: b.y + (position.y * b.h)
    };
  }

  /**
   * Unit direction a line leaves/enters a node along its dominant perimeter axis.
   * Bottom exit (y≈1) → {0, 1}; top (y≈0) → {0, -1}; right (x≈1) → {1, 0}; left → {-1, 0}.
   * Matches the first-segment direction produced by orthogonalizeRoute().
   */
  getConnectionAxisDirection(position) {
    const vertical = Math.min(position.y, 1 - position.y) <= Math.min(position.x, 1 - position.x);
    if (vertical) {
      return { x: 0, y: position.y < 0.5 ? -1 : 1 };
    }
    return { x: position.x < 0.5 ? -1 : 1, y: 0 };
  }

  /**
   * Build padded obstacle rectangles for the A* pathfinder / collision checks.
   * `excludeIds` (the line's own source + target) are not obstacles to themselves.
   * Decision diamonds get extra padding because their corners stick out past the text box.
   */
  buildRoutingObstacles(nodes, excludeIds, padding) {
    return nodes
      .filter(n => !excludeIds.includes(n.id))
      .map(node => {
        const extra = node.type === 'decision' ? 20 : 0;
        const p = padding + extra;
        const b = node.boundingBox;
        return {
          id: node.id,
          x: b.x - p,
          y: b.y - p,
          width: b.w + p * 2,
          height: b.h + p * 2
        };
      });
  }

  /**
   * True if any segment of an orthogonal polyline crosses an obstacle. Used to validate the
   * ACTUAL elbow geometry (not just the straight source→target line) so a clean elbow whose
   * horizontal/vertical leg would cut through a node gets re-routed around it.
   */
  polylineHitsObstacles(points, obstacles) {
    const pf = this.routingSystem.pathfinder;
    if (!pf || typeof pf.checkPathIntersectsObstacles !== 'function') return false;
    for (let i = 0; i < points.length - 1; i++) {
      if (pf.checkPathIntersectsObstacles(points[i], points[i + 1], obstacles)) return true;
    }
    return false;
  }

  /**
   * Clean orthogonal control points for a direct (unobstructed) elbow line between two
   * perimeter points. Mirrors React Flow step edges:
   *  - already axis-aligned → straight line, no control points
   *  - both exit & entry vertical (typical TB branch) → S-bend at the vertical midpoint
   *  - both horizontal (typical LR) → S-bend at the horizontal midpoint
   *  - mixed → a single L-corner that leaves/enters along each node's axis
   */
  simpleElbow(exit, entry, exitVertical, entryVertical) {
    const EPS = 0.5;
    if (Math.abs(exit.x - entry.x) < EPS || Math.abs(exit.y - entry.y) < EPS) {
      return []; // straight segment
    }
    if (exitVertical && entryVertical) {
      const midY = (exit.y + entry.y) / 2;
      return [{ x: exit.x, y: midY }, { x: entry.x, y: midY }];
    }
    if (!exitVertical && !entryVertical) {
      const midX = (exit.x + entry.x) / 2;
      return [{ x: midX, y: exit.y }, { x: midX, y: entry.y }];
    }
    return exitVertical
      ? [{ x: exit.x, y: entry.y }]   // leave vertically, enter horizontally
      : [{ x: entry.x, y: exit.y }];  // leave horizontally, enter vertically
  }

  /**
   * Build orthogonal elbow control points for a Lucid 'elbow' line.
   *
   * Lucid renders an elbow line by connecting endpoint1 → control points → endpoint2
   * with the points as given; it does NOT auto-route, so every consecutive segment must
   * be axis-aligned (share an x or y) or Lucid will draw a diagonal. This routes the line
   * around intermediate nodes using the A* pathfinder (when the direct path is blocked)
   * and then orthogonalizes the full polyline so all segments are 90°.
   *
   * Returns interior control points only (exit/entry endpoints excluded), in absolute
   * page coordinates — the same space as node boundingBoxes.
   */
  calculateElbowControlPoints(transition, nodes, sourcePosition, targetPosition, routingOffset) {
    try {
      const sourceShapeId = this.getShapeId(transition.from);
      const targetShapeId = this.getShapeId(transition.to);
      const sourceNode = nodes.find(n => n.id === sourceShapeId);
      const targetNode = nodes.find(n => n.id === targetShapeId);
      if (!sourceNode || !targetNode) return [];

      const exit = this.getAbsoluteConnectionPoint(sourceNode, sourcePosition);
      const entry = this.getAbsoluteConnectionPoint(targetNode, targetPosition);

      // Direction the line leaves the source / enters the target (along the dominant axis).
      const exitDir = this.getConnectionAxisDirection(sourcePosition);
      const entryDir = this.getConnectionAxisDirection(targetPosition);
      const exitVertical = exitDir.y !== 0;
      const entryVertical = entryDir.y !== 0;

      // Minimum "stub": the line leaves the source (and enters the target) STRAIGHT along the
      // node's axis for a fixed distance before it's allowed to turn. This keeps the exit-point
      // label sitting ON the line (instead of the line jogging sideways immediately) and makes
      // exits/entries look clean and perpendicular. Shortened for very short edges so the two
      // stubs don't overshoot each other. We route between the stub points, not the raw
      // perimeter points, then prepend/append the stubs as control points.
      const span = Math.hypot(entry.x - exit.x, entry.y - exit.y);
      const stub = Math.max(12, Math.min(this.LINE_STUB, span * 0.4));
      const exitStub = { x: exit.x + exitDir.x * stub, y: exit.y + exitDir.y * stub };
      const entryStub = { x: entry.x + entryDir.x * stub, y: entry.y + entryDir.y * stub };

      // First try the clean elbow/S-bend between the stubs (matches the React Flow viewer).
      // CRUCIAL: validate the elbow's ACTUAL orthogonal segments against the other shapes — not
      // the straight source→target line — because an elbow leg can cut through a node the
      // diagonal missed. A 20px clearance also re-routes lines that merely graze a shape.
      const candidate = this.simpleElbow(exitStub, entryStub, exitVertical, entryVertical);
      const cps = [exitStub, ...candidate, entryStub];
      const clearanceObstacles = this.buildRoutingObstacles(nodes, [sourceShapeId, targetShapeId], 20);
      if (!this.polylineHitsObstacles([exit, ...cps, entry], clearanceObstacles)) {
        return cps; // clean elbow is collision-free
      }

      // Clean elbow would cross/graze a shape → route around with A* (forced), between the
      // stubs so the straight exit/entry segments are preserved. Retry with progressively
      // smaller margins so a tight channel still yields a routed path.
      let interior = [];
      for (const pad of [40, 24, 14]) {
        interior = this.calculateLineRouting(transition, routingOffset, nodes, exitStub, entryStub, true, pad) || [];
        if (interior.length > 0) break;
      }
      if (interior.length === 0) {
        return cps; // no channel found anywhere; keep the clean elbow (with stubs) as best effort
      }
      const points = this.orthogonalizeRoute(exitStub, entryStub, interior, exitVertical, entryVertical);
      return [exitStub, ...this.simplifyJoints(points, 8), entryStub];
    } catch (error) {
      this.log('debug', 'Elbow control point calculation failed:', error.message);
      return [];
    }
  }

  /**
   * Turn a sequence [exit, ...interior, entry] into an orthogonal polyline by inserting
   * an L-corner between any two points that aren't already axis-aligned. The first corner
   * preserves the node's exit axis; the last corner makes the line approach the target
   * along its entry axis. Returns the interior corners only (endpoints excluded).
   */
  orthogonalizeRoute(exit, entry, interior, exitVertical, entryVertical) {
    const EPS = 0.5;
    const aligned = (a, b) => Math.abs(a.x - b.x) < EPS || Math.abs(a.y - b.y) < EPS;

    const waypoints = [exit, ...interior, entry];
    const result = [exit];

    for (let i = 1; i < waypoints.length; i++) {
      const prev = result[result.length - 1];
      const next = waypoints[i];
      if (aligned(prev, next)) {
        result.push(next);
        continue;
      }
      // Need an L-corner. Decide horizontal-first vs vertical-first.
      const isFirstSegment = i === 1;
      const isLastSegment = i === waypoints.length - 1;
      let verticalFirst;
      if (isFirstSegment) {
        verticalFirst = exitVertical;        // leave the source node along its exit axis
      } else if (isLastSegment) {
        verticalFirst = !entryVertical;       // arrive at target along its entry axis
      } else {
        verticalFirst = true;                 // consistent interior bends
      }
      const corner = verticalFirst
        ? { x: prev.x, y: next.y }
        : { x: next.x, y: prev.y };
      result.push(corner);
      result.push(next);
    }

    // Strip the endpoints — Lucid supplies those from endpoint1/endpoint2.
    return result.slice(1, -1);
  }

  /**
   * Calculate intelligent line routing points using A* pathfinding
   * This properly integrates the EdgeRoutingSystem's A* pathfinder with Lucid export
   */
  calculateLineRouting(transition, offset, nodes, startPoint = null, endPoint = null, forceRoute = false, routePadding = null) {
    try {
      // Find source and target nodes using shape IDs
      const sourceShapeId = this.getShapeId(transition.from);
      const targetShapeId = this.getShapeId(transition.to);

      const sourceNode = nodes.find(n => n.id === sourceShapeId);
      const targetNode = nodes.find(n => n.id === targetShapeId);

      if (!sourceNode || !targetNode) {
        this.log('debug', 'Source or target node not found for routing');
        return [];
      }

      // Route between the actual perimeter connection points when provided (so the
      // path lines up with the line's real endpoints); otherwise fall back to centers.
      const sourcePoint = startPoint
        ? { x: startPoint.x, y: startPoint.y }
        : {
            x: sourceNode.boundingBox.x + (sourceNode.boundingBox.w / 2),
            y: sourceNode.boundingBox.y + (sourceNode.boundingBox.h / 2)
          };

      const targetPoint = endPoint
        ? { x: endPoint.x, y: endPoint.y }
        : {
            x: targetNode.boundingBox.x + (targetNode.boundingBox.w / 2),
            y: targetNode.boundingBox.y + (targetNode.boundingBox.h / 2)
          };

      // Apply offset for parallel lines
      if (offset && Math.abs(offset) > 5) {
        sourcePoint.x += offset;
        targetPoint.x += offset;
      }

      // Convert nodes to obstacles for A* pathfinding. Padding is the routing margin; callers
      // can override it (e.g. retry with a smaller margin to fit through a tight channel).
      const nodePadding = routePadding != null
        ? routePadding
        : (this.routingSystem.pathfinder?.options?.nodePadding || 40);
      const obstacles = this.buildRoutingObstacles(nodes, [sourceShapeId, targetShapeId], nodePadding);

      // When not forced, skip routing if the straight path is already clear (cleaner look).
      // When forced (the caller already found the clean elbow collides), always route.
      if (!forceRoute) {
        const pathIntersectsObstacles = this.routingSystem.pathfinder?.checkPathIntersectsObstacles?.(
          sourcePoint,
          targetPoint,
          obstacles
        );
        if (!pathIntersectsObstacles) {
          this.log('debug', 'Direct path is clear, skipping routing');
          return [];
        }
      }

      // Path has obstacles - need to calculate route around them
      this.log('debug', 'Obstacles detected in direct path, calculating route');

      // Use A* pathfinder to find obstacle-avoiding path
      const path = this.routingSystem.pathfinder?.findPath?.(
        sourcePoint,
        targetPoint,
        obstacles
      );

      if (!path || path.length <= 2) {
        this.log('debug', 'No routing path found or path too simple');
        return [];
      }

      // Convert path to Lucid joints (exclude first and last points - those are the endpoints)
      const joints = path.slice(1, -1).map(point => ({
        x: point.x,
        y: point.y
      }));

      // Simplify joints to avoid too many waypoints (max 6 joints for clean appearance)
      const simplifiedJoints = this.simplifyJoints(joints, 6);

      this.log('debug', `Calculated ${simplifiedJoints.length} routing waypoints for line ${sourceShapeId} -> ${targetShapeId}`);

      return simplifiedJoints;

    } catch (error) {
      this.log('debug', 'Line routing calculation failed:', error.message);
      return []; // Return empty array - Lucid will draw direct curved line
    }
  }

  /**
   * Simplify joints by removing nearly collinear points
   * Keeps path quality while reducing waypoint count for cleaner appearance
   */
  simplifyJoints(joints, maxJoints = 6) {
    if (joints.length <= maxJoints) return joints;

    // Use Douglas-Peucker-like algorithm to reduce points
    const simplified = [];
    const angleThreshold = 0.174; // ~10 degrees in radians

    simplified.push(joints[0]); // Always keep first point

    for (let i = 1; i < joints.length - 1; i++) {
      const prev = simplified[simplified.length - 1];
      const current = joints[i];
      const next = joints[i + 1];

      // Calculate angle between prev->current and current->next
      const angle1 = Math.atan2(current.y - prev.y, current.x - prev.x);
      const angle2 = Math.atan2(next.y - current.y, next.x - current.x);
      const angleDiff = Math.abs(angle2 - angle1);

      // Keep point if it represents a significant direction change
      if (angleDiff > angleThreshold) {
        simplified.push(current);
      }

      // Stop if we've reached the max
      if (simplified.length >= maxJoints - 1) break;
    }

    simplified.push(joints[joints.length - 1]); // Always keep last point

    return simplified;
  }

  /**
   * Get line color based on transition type
   */
  getLineColor(transitionType) {
    const colors = {
      success: '#4CAF50',
      error: '#F44336', 
      condition: '#2196F3',
      default: '#666666'
    };
    return colors[transitionType] || colors.default;
  }

  /**
   * Get line type based on transition type
   *
   * Uses 'elbow' for all transition types. This is REQUIRED for Lucid to honor our
   * obstacle-avoiding routing: per the Lucid Standard Import spec, `elbowControlPoints`
   * are only respected on `lineType: 'elbow'` lines (and `joints` only on 'straight'
   * lines). A 'curved' line ignores both, so Lucid would draw a naive perimeter-to-
   * perimeter curve that cuts through intermediate nodes. Elbow also matches the
   * orthogonal style of the React Flow Contact Flow Viewer.
   */
  getLineType(transitionType) {
    return 'elbow';
  }

  /**
   * Get all actions
   */
  getActions() {
    const actionsArray = Array.from(this.actions.values());
    
    if (actionsArray.length === 0) {
      this.log('warn', 'No actions found in contact flow');
    }
    
    return actionsArray;
  }

  /**
   * Get single action by identifier
   */
  getAction(identifier) {
    return this.actions.get(identifier);
  }

  /**
   * Get all transitions including entry transition
   */
  getAllTransitions() {
    const allTransitions = [];
    
    // Add entry to start transition
    if (this.startAction) {
      allTransitions.push({
        type: 'success',
        from: 'entry-node',
        to: this.startAction,
        label: 'Start',
        priority: 1
      });
    } else {
      this.log('warn', 'No start action found - entry node will be isolated');
    }
    
    // Add regular transitions
    const actions = this.getActions();
    
    actions.forEach(action => {
      const transitions = this.getTransitionsForAction(action);
      
      if (transitions.length === 0) {
        this.log('debug', `Action ${action.Identifier} (${action.Type}) has no outgoing transitions (normal for end nodes)`);
      }
      
      allTransitions.push(...transitions);
    });
    
    // Validate that all transition targets exist and count valid vs invalid
    const actionIds = new Set(actions.map(a => a.Identifier));
    actionIds.add('entry-node'); // Add entry node to valid targets
    
    const validTransitions = [];
    const invalidTransitions = [];
    
    allTransitions.forEach(transition => {
      if (actionIds.has(transition.from) && actionIds.has(transition.to)) {
        validTransitions.push(transition);
      } else {
        invalidTransitions.push(transition);
      }
    });
    
    // Report validation results
    if (invalidTransitions.length > 0) {
      this.log('warn', `Found ${invalidTransitions.length} transitions referencing non-existent nodes:`);
      invalidTransitions.forEach(t => {
        const fromExists = actionIds.has(t.from);
        const toExists = actionIds.has(t.to);
        this.log('warn', `  ${t.from} -> ${t.to} (from: ${fromExists ? 'exists' : 'missing'}, to: ${toExists ? 'exists' : 'missing'})`);
      });
    }
    
    this.log('info', `Transition validation: ${validTransitions.length} valid, ${invalidTransitions.length} invalid/skipped`);
    
    return validTransitions; // Return only valid transitions
  }

  /**
   * Get transitions for a specific action
   */
  getTransitionsForAction(action) {
    const transitions = [];
    
    if (!action.Transitions) return transitions;

    // Helper function to validate transition target
    const isValidTarget = (target) => {
      return target && 
             typeof target === 'string' && 
             target.trim() !== '' && 
             target !== 'undefined' && 
             target !== 'null';
    };

    // Handle different transition types based on action type
    const actionType = action.Type;
    let transitionIndex = 0;

    // Success transitions (for most action types)
    const noSuccessTypes = [
      'CheckHoursOfOperation', 'CheckMetricData', 'Compare',
      'GetParticipantInput', 'ConnectParticipantWithLexBot',
      'DistributeByPercentage', 'Loop', 'TransferContactToQueue',
      'ShowView', 'Wait'
    ];

    if (action.Transitions.NextAction && !noSuccessTypes.includes(actionType)) {
      if (isValidTarget(action.Transitions.NextAction)) {
        transitions.push({
          type: 'success',
          from: action.Identifier,
          to: action.Transitions.NextAction,
          label: 'Success',
          priority: 1,
          handleIndex: transitionIndex++
        });
      } else {
        this.log('warn', `Action ${action.Identifier} (${actionType}) has invalid NextAction: ${action.Transitions.NextAction}`);
      }
    }

    // Special handling for GetParticipantInput with StoreInput
    if (actionType === 'GetParticipantInput') {
      const storeInput = action.Parameters?.StoreInput === 'True' || action.Parameters?.StoreInput === true;
      if (storeInput && action.Transitions.NextAction) {
        if (isValidTarget(action.Transitions.NextAction)) {
          transitions.push({
            type: 'success',
            from: action.Identifier,
            to: action.Transitions.NextAction,
            label: 'Success',
            priority: 1,
            handleIndex: transitionIndex++
          });
        } else {
          this.log('warn', `GetParticipantInput action ${action.Identifier} has invalid NextAction: ${action.Transitions.NextAction}`);
        }
      }
    }

    // Error transitions
    if (action.Transitions.Errors) {
      action.Transitions.Errors.forEach(error => {
        if (isValidTarget(error.NextAction)) {
          transitions.push({
            type: 'error',
            from: action.Identifier,
            to: error.NextAction,
            label: ContactFlowParsingUtils.getErrorLabel(error.ErrorType),
            priority: 3,
            handleIndex: transitionIndex++
          });
        } else {
          this.log('warn', `Action ${action.Identifier} (${actionType}) has invalid error transition for ${error.ErrorType}: ${error.NextAction}`);
        }
      });
    }

    // Condition transitions
    if (action.Transitions.Conditions) {
      action.Transitions.Conditions.forEach((condition, index) => {
        if (isValidTarget(condition.NextAction)) {
          transitions.push({
            type: 'condition',
            from: action.Identifier,
            to: condition.NextAction,
            label: ContactFlowParsingUtils.getConditionLabel(condition, index, actionType),
            priority: 2,
            handleIndex: transitionIndex++
          });
        } else {
          this.log('warn', `Action ${action.Identifier} (${actionType}) has invalid condition transition ${index}: ${condition.NextAction}`);
        }
      });
    }

    return transitions;
  }

  /**
   * Get flow summary for metadata
   */
  getFlowSummary() {
    const actions = this.getActions();
    const transitions = this.getAllTransitions();
    
    const blockTypes = {};
    actions.forEach(action => {
      blockTypes[action.Type] = (blockTypes[action.Type] || 0) + 1;
    });

    return {
      version: this.flow.Version,
      startAction: this.startAction,
      actionCount: actions.length,
      transitionCount: transitions.length,
      hasPositionData: Object.keys(this.metadata).length > 0,
      blockTypes
    };
  }
}